import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { CheckCircle2, Home as HomeIcon, Shield, TrendingUp, GraduationCap, Building2, Mail, FileText, Twitter, Facebook, Link2, Linkedin, MessageSquare, Download, Cpu, ShoppingBag, Calendar, Clock, MapPin, Phone, ExternalLink } from 'lucide-react';
import RecapCarousel from '../components/RecapCarousel';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  // Hash-based deep linking for article + announcement modals
  const VALID_SLUGS = ['economic-reality', 'education-and-opportunity', 'jobs-workforce-small-business', 'midterms-stress-test', 'housing-crisis', 'public-safety', 'democratic-party-breaking-apart', 'generational-change', 'medical-marijuana-texas', 'voting-rights-act', 'ballot-on-time', 'birthright-citizenship', 'campaign-finance', 'dc-taxes-hr9720', 'endorsement-nixon', 'endorsement-talarico', 'endorsement-plaskett', 'endorsement-crowley', 'endorsement-bottoms', 'event-leadership-reception', 'event-vip-reception'];

  // Feature flag: hides the Executive Director announcement + profile sections.
  // Set to true to restore Derrell Simpson content site-wide.
  const SHOW_ED_CONTENT = false;

  const events = [
    {
      id: 'vip-reception-2026-09-18',
      slug: 'event-vip-reception',
      kind: 'VIP',
      accessBadge: 'Private · Invitation Only',
      status: 'UPCOMING',
      eyebrow: 'Washington, D.C. | September 18',
      title: 'VIP Leadership Reception',
      tagline: 'An Intimate Gathering. A Powerful Purpose. The Fight for Florida.',
      hostedByIntro: 'Hosted by',
      hostedBy: [
        { name: 'U.S. Senator Angela Alsobrooks', role: '' },
        { name: 'Cora Masters Barry', role: 'Forever Washington, D.C. First Lady' },
      ],
      featuredGuest: 'Angie Nixon',
      guestTitle: [
        'Democratic Nominee for United States Senate',
      ],
      date: 'Friday, September 18, 2026',
      time: '3:30 PM to 4:30 PM',
      venue: 'United Negro College Fund',
      address: '1805 7th Street, NW\nWashington, DC 20001',
      image: '/event-vip-banner.jpg',
      description: [
        'Angie Nixon is taking on one of the toughest political fights in the country, in a state Democrats have struggled to win, and she is doing it as a Black woman already facing racist attacks for stepping into power.',
        'CommonSense Democrats invites you to join United States Senator Angela Alsobrooks and Forever Washington, DC First Lady Cora Masters Barry for a private reception with Angie Nixon, Democratic nominee for U.S. Senate in Florida.',
        'Come meet Angie, hear directly from her, and join a room of people prepared to invest in the Fight for Florida.',
      ],
      attendanceNote: 'Attendance at the VIP Leadership Reception is available to invited guests with a suggested contribution beginning at $500.',
      contributionLevels: ['$500', '$1,000', '$2,500', '$3,500'],
      ctaLabel: 'Contribute to Attend',
      ctaUrl: 'https://secure.actblue.com/donate/call.me.senator.commonsense.vip',
      ctaOpensNewTab: false,
      rsvpUrl: 'https://forms.gle/vcNiRDFuvy4ECrnF6',
    },
    {
      id: 'leadership-reception-2026-09-18',
      slug: 'event-leadership-reception',
      kind: 'PUBLIC',
      accessBadge: 'Public Event',
      status: 'UPCOMING',
      eyebrow: 'Washington, D.C. | September 18',
      title: 'A New Way to Democrat featuring Representative Angie Nixon',
      shortTitle: 'A New Way to Democrat',
      tagline: 'Building the Next Generation of Democratic Leadership',
      featuredGuest: 'The Honorable Angie Nixon',
      guestTitle: [
        'Florida Democratic Nominee for the United States Senate',
        'Florida State Representative',
      ],
      additionalGuestsIntro: 'Joining us will be a coalition of Democratic leaders from across the country, including:',
      additionalGuests: [
        'Hon. Sputty Cephas (MD)',
        'Hon. Brian Cunningham (NY)',
        'Hon. Hamilton Grant (SC)',
        'Hon. Jeremy Gray (AL)',
        'Hon. Chioma Iwuoha (DC)',
        'Hon. Shireka McCarthy (MD)',
        'Hon. Oye Owolewa (DC)',
        'Hon. Zachary Parker (DC)',
        'Hon. Jacque Patterson (DC)',
        'Hon. Denise Roberts (MD)',
        'Hon. Keith Sydnor (MD)',
        'Hon. Deon Tedder (SC)',
        'Hon. Latasha Ward (MD)',
        'Hon. Rashiem Wright (MD)',
        'Hon. RaShon Young (FL)',
      ],
      image: '/event-angie-nixon-banner.jpg',
      date: 'Friday, September 18, 2026',
      time: '3:00 PM to 6:00 PM',
      venue: 'Urban Garden Brewing',
      address: '624 T Street NW\nWashington, DC 20001',
      description: [
        'Join CommonSense Democrats, Representative Angie Nixon, Members of Congress, civic leaders and Democratic leaders from across the country for an afternoon focused on the future of our party and the next generation of Democratic leadership.',
        'As Democrats look toward the elections ahead and the future of the party, this reception will bring together leaders committed to strengthening our coalition, developing the next generation of public servants and building a Democratic Party capable of competing and governing across the country.',
      ],
      ctaLabel: 'Support Angie',
      ctaUrl: 'https://secure.actblue.com/donate/common.sense.call.me.senator',
      rsvpUrl: 'https://forms.gle/1XBcFhPCFhUz9HSL7',
      sponsorshipUrl: 'https://forms.gle/9xvmQ994okmaGAKf8',
    },
  ];
  const [openModal, setOpenModal] = useState(null);
  const [expandedPolicies, setExpandedPolicies] = useState({});

  // Live day-of state for the "A New Way to Democrat" event (ends 2026-09-18 6:00 PM ET)
  const EVENT_END_TS = Date.parse('2026-09-18T18:00:00-04:00');
  const [isEventPast, setIsEventPast] = useState(() => Date.now() >= EVENT_END_TS);

  useEffect(() => {
    if (isEventPast) return;
    const msUntilEnd = EVENT_END_TS - Date.now();
    if (msUntilEnd <= 0) { setIsEventPast(true); return; }
    // Cap the timeout at ~24 days (Node/browser safe) and re-check hourly otherwise.
    const timer = setTimeout(() => setIsEventPast(true), Math.min(msUntilEnd, 24 * 24 * 60 * 60 * 1000));
    return () => clearTimeout(timer);
  }, [isEventPast, EVENT_END_TS]);

  useEffect(() => {
    const applyHash = () => {
      const slug = (window.location.hash || '').replace(/^#/, '');
      if (slug && VALID_SLUGS.includes(slug)) {
        setOpenModal(slug);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  // Scroll to in-page section anchors (e.g. #sponsors, #host-committee, #recap)
  // once the corresponding element is actually mounted. This is required
  // because those sections live inside {isEventPast && ...} and are not part
  // of the initial HTML, so the browser's native anchor-scroll misses them.
  // We also RETRY the scroll for ~2s to correct for late-loading content
  // (RecapCarousel manifest fetch, image loads) that changes page height and
  // would leave a one-shot scrollIntoView pointed at the wrong y.
  useEffect(() => {
    const SECTION_IDS = new Set(['sponsors', 'host-committee', 'recap']);
    let retryTimer = null;
    const scrollToHash = () => {
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      const id = (window.location.hash || '').replace(/^#/, '').toLowerCase();
      const baseId = id.split('-photo-')[0];
      if (!SECTION_IDS.has(id) && !SECTION_IDS.has(baseId)) return;
      const targetId = SECTION_IDS.has(id) ? id : baseId;

      let attempts = 0;
      const maxAttempts = 10; // ~2s total (10 * 200ms)
      const align = () => {
        const target = document.getElementById(targetId);
        if (!target) return; // section not mounted yet
        target.scrollIntoView({ behavior: attempts === 0 ? 'auto' : 'smooth', block: 'start' });
        attempts += 1;
        if (attempts < maxAttempts) {
          retryTimer = setTimeout(align, 200);
        }
      };
      // First attempt on next paint so React has committed the sections.
      requestAnimationFrame(align);
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    // Re-run once the DOM is fully idle (all images/manifests should be in).
    window.addEventListener('load', scrollToHash);
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('hashchange', scrollToHash);
      window.removeEventListener('load', scrollToHash);
    };
  }, [isEventPast]);

  const handleModalChange = (slug, open) => {
    if (open) {
      setOpenModal(slug);
      if (window.location.hash !== `#${slug}`) {
        window.history.replaceState(null, '', `#${slug}`);
      }
    } else {
      setOpenModal(null);
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  };

  // Always share the canonical production domain so social previews show
  // "commonsensedemocrats.us" — not the preview hostname. Share links point
  // at /api/article/<slug> (proxied to FastAPI, not touched by the platform's
  // bot-UA SPA rewrite) so social crawlers receive per-article OG meta tags.
  const PUBLIC_SITE_URL = 'https://commonsensedemocrats.us';
  const shareUrlFor = (slug) => {
    return slug ? `${PUBLIC_SITE_URL}/api/article/${slug}` : `${PUBLIC_SITE_URL}/`;
  };

  const renderEndorsementShare = (slug, title) => {
    // Route to the proper indexable backend URL so social crawlers get
    // per-item OG meta tags and Google indexes each as a distinct page.
    let shareUrl;
    if (slug.startsWith('endorsement-')) {
      shareUrl = `${PUBLIC_SITE_URL}/api/endorsement/${slug}`;
    } else if (slug.startsWith('event-')) {
      shareUrl = `${PUBLIC_SITE_URL}/api/events/${slug}`;
    } else {
      shareUrl = `${PUBLIC_SITE_URL}/#${slug}`;
    }
    const shareText = `CommonSense Democrats: ${title}`;
    return (
      <div className="border-t border-slate-700 pt-4 mt-6 flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Share:</span>
        <a
          data-testid={`share-x-${slug}`}
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
          aria-label="Share on X"
        >
          <Twitter className="w-4 h-4" /> X
        </a>
        <a
          data-testid={`share-facebook-${slug}`}
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
          aria-label="Share on Facebook"
        >
          <Facebook className="w-4 h-4" /> Facebook
        </a>
        <a
          data-testid={`share-linkedin-${slug}`}
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
          aria-label="Share on LinkedIn"
        >
          <Linkedin className="w-4 h-4" /> LinkedIn
        </a>
        <a
          data-testid={`share-email-${slug}`}
          href={`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
          aria-label="Share via Email"
        >
          <Mail className="w-4 h-4" /> Email
        </a>
        <a
          data-testid={`share-sms-${slug}`}
          href={`sms:?&body=${encodeURIComponent(shareText + ' ' + shareUrl)}`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
          aria-label="Share via SMS"
        >
          <MessageSquare className="w-4 h-4" /> SMS
        </a>
        <button
          data-testid={`share-copy-${slug}`}
          type="button"
          onClick={() => {
            if (navigator.clipboard) {
              navigator.clipboard.writeText(shareUrl);
            }
            toast({ title: 'Link copied', description: 'Share it with your network.' });
          }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
          aria-label="Copy link"
        >
          <Link2 className="w-4 h-4" /> Copy Link
        </button>
      </div>
    );
  };

  const handleNewsletterSignup = (e) => {
    e.preventDefault();
    if (email) {
      toast({
        title: "Thank you for joining!",
        description: "You've been added to our mailing list.",
      });
      setEmail('');
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (contactForm.name && contactForm.email && contactForm.message) {
      // In production, this will send to hello@commonsensedemocrats.us
      toast({
        title: "Message sent!",
        description: "We'll get back to you soon.",
      });
      setContactForm({ name: '', email: '', message: '' });
    }
  };

  const policyPriorities = [
    {
      icon: <HomeIcon className="w-8 h-8" />,
      title: "Affordable Housing and Urban Development",
      points: [
        "Streamline housing approvals to fast-track affordable housing without sacrificing meaningful community input",
        "Promote balanced policies that protect homeowners while expanding rental opportunities",
        "Invest in sustainable urban planning that benefits residents, workers, small businesses, and families, not just developers"
      ]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Public Safety and Smart Criminal Justice Reform",
      points: [
        "Implement community-based policing models to rebuild trust and improve safety",
        "Expand alternative response programs for nonviolent crises, including mental health incidents and substance-related emergencies",
        "Support crime prevention initiatives that focus on youth intervention, job creation, treatment access, and neighborhood stability"
      ]
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Economic Growth and Small Business Development",
      points: [
        "Reduce bureaucratic barriers to encourage entrepreneurship while maintaining responsible oversight",
        "Invest in workforce training and apprenticeship programs that prepare workers for modern industries, including clean energy, construction, advanced manufacturing, cybersecurity, health care, and AI-enabled business operations",
        "Provide targeted tax incentives for businesses that create local jobs, pay fair wages, and reinvest in their communities"
      ]
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Education and Workforce Development",
      points: [
        "Increase funding for public schools while ensuring financial accountability and measurable outcomes",
        "Expand access to STEM, vocational, and trade education so students have multiple career pathways",
        "Teach practical digital and AI literacy so students, workers, and small business owners can use new tools safely, productively, and competitively",
        "Reduce overreliance on standardized testing while strengthening curricula that promote problem-solving, critical thinking, communication, and civic responsibility"
      ]
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Strengthening Local Governance and Representation",
      points: [
        "Advocate for Washington, D.C. statehood to secure full voting rights and representation",
        "Enhance infrastructure investments to improve public transportation, sustainability, grid reliability, water systems, and broadband access",
        "Encourage greater municipal control so local governments can tailor policies to their needs while protecting fundamental rights"
      ]
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Artificial Intelligence, Technology, and Data Centers",
      intro: "CommonSense Democrats reject the false choice between technological leadership and public accountability. America should lead the world in AI and advanced technology, but that leadership must be built on trust, transparency, strong communities, competitive markets, reliable infrastructure, and real benefits for working families.",
      groups: [
        {
          label: "AI, Innovation, and Workers",
          points: [
            "Lead in responsible AI innovation by supporting research, startups, small business adoption, public-private partnerships, and American competitiveness while requiring clear standards for safety, security, accountability, and human oversight",
            "Protect privacy, civil rights, and due process by opposing black-box government decisions in policing, benefits, housing, health care, education, employment, and immigration. When AI affects a person's rights, safety, or livelihood, people deserve notice, explanation, human review, and a meaningful appeal process",
            "Modernize government with practical technology by using AI and automation to reduce backlogs, improve constituent services, detect waste and fraud, and make public agencies more responsive, without replacing public accountability with vendor-driven systems",
            "Prepare workers for the AI economy through reskilling, apprenticeships, community college programs, portable credentials, and small business training so technology creates opportunity instead of leaving workers behind"
          ]
        },
        {
          label: "Data Centers, Energy, and Cybersecurity",
          points: [
            "Treat data centers as critical infrastructure with serious planning for electricity demand, transmission capacity, backup power, water use, land use, emergency resilience, cybersecurity, and local ratepayer protection",
            "Require community benefit from major data center projects including local hiring, apprenticeship pathways, tax transparency, infrastructure contributions, water and energy efficiency commitments, and clear agreements that protect residents from subsidizing private profits",
            "Build clean, reliable energy alongside digital growth by pairing data center expansion with new generation, storage, transmission, efficiency standards, and demand management so AI growth does not weaken grid reliability or raise household energy costs",
            "Defend open competition and small business access by preventing dominant technology platforms from using monopoly power, opaque algorithms, or data lock-in to crush startups, creators, local publishers, and small businesses",
            "Strengthen cybersecurity and election integrity by protecting public systems, campaigns, voter data, schools, hospitals, utilities, and small businesses from ransomware, deepfakes, identity theft, and foreign interference",
            "Use technology to expand democracy, not manipulate it by supporting transparency for AI-generated political content, clear disclosure in digital advertising, stronger protections against deceptive deepfakes, and modern tools that help voters access accurate information"
          ]
        }
      ]
    }
  ];

  const donorTiers = [
    {
      name: "Advocate Circle",
      amount: "Up to $50,000",
      description: "Support our mission with meaningful contributions"
    },
    {
      name: "Leadership Circle",
      amount: "$50,001–$250,000",
      description: "Join strategic discussions and shape our direction"
    },
    {
      name: "Strategy Council",
      amount: "$250,001–$500,000",
      description: "Non-voting member with insider access to campaign strategy"
    },
    {
      name: "National Strategy Board",
      amount: "$500,000 and above",
      description: "Voting member with direct influence on national priorities"
    }
  ];

  const articles = [
    {
      id: 1,
      slug: "economic-reality",
      image: "/articles/economic-reality.jpg",
      title: "The Economic Reality Nobody Can Ignore",
      excerpt: "Every election cycle, candidates try to control the conversation. But heading into the 2026 midterms, there is no clever way around the central issue in American life...",
      content: `Every election cycle, candidates try to control the conversation. They point backward when the past helps them, change the subject when the present gets uncomfortable, and promise a future that sounds better than the lives people are actually living.

But heading into the 2026 midterms, there is no clever way around the central issue in American life. The economy is the barrier standing between working families and real stability. It is the reason young adults are delaying major life decisions. It is the reason small businesses cannot find or keep enough workers. It is the reason parents feel trapped between income that looks decent on paper and bills that never stop rising.

This is the paradox of modern America. We live in a country of extraordinary productivity, innovation, and wealth creation, yet millions of the people doing the work, solving the problems, building the companies, staffing the classrooms, caring for the sick, and keeping local economies alive cannot afford the basic architecture of a stable life.

Housing is out of reach in too many communities. Childcare has become a second mortgage for working parents. Groceries take a bigger bite every month. Health care remains a financial threat even for families with insurance. Transportation, utilities, rent, and debt are no longer separate pressures. Together, they have become the daily mathematics of American anxiety.

That anxiety is reshaping the country.

It affects whether people marry, whether they have children, whether they stay in the city where they grew up, whether they take a risk on a new business, whether they go back to school, whether they accept a job, and whether they believe politics has anything useful to offer them at all. A nation cannot call itself strong when the people most capable of building its future are forced to postpone their own.

Add in labor shortages, uneven wage growth, and the disruption of artificial intelligence, and the challenge becomes even more serious. Small businesses are not struggling because they lack ambition. Many are struggling because the cost of labor, rent, insurance, supplies, and capital keeps rising at the same time customers are becoming more cautious. Workers are not asking for luxury. They are asking for wages that can support rent, food, childcare, transportation, and a little room to breathe.

Young people are told to start at the bottom, but the bottom no longer covers the basics. Parents are told to work harder, but many are already working more than one job. Retirees are returning to work because retirement is no longer financially secure. Employers say they cannot find workers, while workers say they cannot afford the jobs being offered. Both can be true. That is what happens when the cost structure of daily life breaks faster than the political system responds.

President Trump built his political brand on economic confidence, but confidence is not the same as competence. Swagger does not lower rent. Blame does not cut grocery bills. Tariffs, chaos, and political theater do not create the kind of long-term certainty families and businesses need. If working people continue to feel squeezed, no amount of branding will hide the gap between what voters were promised and what they are experiencing.

But Democrats should not mistake Republican weakness for Democratic strength.

Voters are not looking for a party that simply points across the aisle and says, "They made it worse." They are looking for leaders who understand the pressure in their lives and can explain what they will actually do about it. Congressional gridlock may be real, but it is not an excuse that pays anyone's rent. Families do not care which committee stalled the bill. Small business owners do not care who won the cable news argument. They want results.

That is the danger for every elected official heading into 2026. Members of Congress risk looking like they are occupying seats instead of solving problems. The country is moving too quickly for leaders who treat polarization as a permanent hiding place. The economy does not care about party talking points. Neither do voters when their bills are due.

The hard truth is simple. Any candidate asking to be elected or re-elected needs two things.

First, a little amnesia. Not the kind that forgets values, history, or accountability, but the kind that lets leaders move beyond stale fights, recycled slogans, and the permanent outrage machine. Voters have heard the old arguments. They know the blame. They live with the consequences every day. What they need now is not another performance of political grievance. They need leadership that can move.

Second, vision. Not vague optimism. Not another speech about the middle class with no plan attached. Vision means a serious agenda for affordability, housing, childcare, wages, workforce development, small business growth, health care costs, public safety, and education. It means understanding that economic policy is family policy, public safety policy, education policy, and community stability policy all at once.

A serious economic agenda must begin with the lives people are actually living.

It should make it easier to build housing where workers need to live. It should expand childcare capacity so parents can work without going broke. It should support apprenticeships, trade programs, community colleges, and workforce pipelines that connect people to jobs that exist now, not just jobs politicians like to mention in speeches. It should help small businesses compete without drowning in compliance costs, rent hikes, and financing barriers. It should reward work, responsibility, and local problem-solving.

This is where CommonSense Democrats stand.

We are not interested in politics that treats working families as props. We are not here to defend a broken status quo because it happens to wear the right party label. We believe the Democratic Party must be the party of practical results, community-rooted leadership, and economic common sense. That means supporting candidates who understand that affordability is not an abstract policy category. It is the lived experience of millions of Americans trying to hold their lives together.

The next generation does not need more excuses. It needs leaders who can build. Families do not need another round of partisan theater. They need breathing room. Small businesses do not need speeches about entrepreneurship. They need conditions that allow them to survive, hire, grow, and serve their communities.

The hurdle is the economy. The only way over it is forward.

If you know a local leader with the courage to step up and solve real problems, someone grounded in community, not partisanship, nominate them to run for office at CommonSenseDemocrats.us.

Let's put real leaders where they belong: at the table.`,
      author: "CommonSense Democrats"
    },
    {
      id: 2,
      slug: "education-and-opportunity",
      image: "/articles/education-and-opportunity.jpg",
      title: "What Happens When We Don't Elect CommonSense Democrats?",
      excerpt: "Education is often discussed as if it begins and ends inside a classroom. But education is bigger than a building...",
      content: `Education is often discussed as if it begins and ends inside a classroom. Politicians talk about test scores, books, teachers, budgets, and school boards. Those things matter, but they do not tell the whole story. Education is bigger than a building. It is one of the foundations that allows people to work, raise families, build careers, start businesses, and believe that their future can be better than their present.

For millions of Americans, education means access. It means a parent can go to work because their child has a safe place to learn during the day. It means a teenager can graduate, enter a trade program, attend community college, join the military, or step into a first job with a real chance to grow. It means an adult who lost work can retrain. It means a veteran can move from service to civilian life with a pathway to stability. It means a child born in the wrong zip code is not automatically locked out of opportunity.

That is why elections matter.

When we fail to elect leaders who understand education as a pillar of economic life, the consequences do not always show up immediately in national headlines. They show up quietly, then all at once. A school closes. A bus route disappears. A teacher leaves and is not replaced. An afterschool program gets cut. A counselor's caseload doubles. A career training program loses funding. A parent misses work because childcare fell through. A student who needed help falls further behind.

These are not isolated problems. They are connected failures.

When a public school closes because of budget cuts, bad planning, neglect, or political gamesmanship, it does not just close a building. It removes a daily support system from the community. The cafeteria that fed hundreds of children is gone. The afterschool program that kept students safe and engaged is gone. The counselor who helped students apply for college, apprenticeships, scholarships, or job training is gone. The early childhood program that helped parents keep steady hours is gone. The classroom where a struggling eighth grader might have discovered science, coding, construction, nursing, or engineering is gone.

A school is often the most important piece of civic infrastructure in a neighborhood. When it weakens, families feel it. Employers feel it. Local businesses feel it. Public safety feels it. The entire community absorbs the damage.

That is what gets lost when education becomes another battlefield for political noise.

Too many politicians want to turn schools into stages for ideological performance. They want to pick fights over books, slogans, and cable news talking points, but they have far less to say about teacher shortages, school funding, mental health support, career pathways, childcare access, special education, and whether graduates are prepared for the jobs that actually exist. They know how to create outrage. They do not know how to create opportunity.

Families deserve better than that.

A young mother in Arizona does not need a school board member trying to build a media profile. She needs a reliable school day so she can work her hospital shift. A high school senior in Michigan does not need adults treating education like a political prop. He needs the credits, counseling, and training pathway that can help him enter a skilled trade and earn a paycheck. A veteran in Georgia does not need speeches about patriotism. He needs a serious bridge from service to career, whether that means the GI Bill, a certificate program, an apprenticeship, or a second chance in a new industry.

Without strong schools and practical education policy, these stories become harder to write. Opportunity becomes less available. Work becomes less stable. Families become more fragile.

The simple truth is this: if people cannot access education, they cannot fully access opportunity. And when opportunity disappears, the American Dream becomes a closed door.

CommonSense Democrats believe education should be treated as one of the central engines of American prosperity. That means funding schools responsibly, supporting teachers, protecting students, expanding career and technical education, strengthening community colleges, investing in apprenticeships, and making sure adults can retrain when the economy changes. Education should not stop at childhood. It should remain available throughout life, especially in a country where technology, automation, and industry disruption are changing the nature of work.

This is not about defending a system because it exists. Some schools need reform. Some programs need accountability. Some budgets need closer scrutiny. Some districts need better leadership. But reform should mean making education more useful, more accessible, and more connected to real life. It should not mean starving schools, attacking teachers, closing doors, or letting politics replace problem-solving.

When we do not elect CommonSense Democrats, we get leaders who mistake conflict for courage. We get school boards filled with extremists who know how to ban a book but not how to balance a budget. We get politicians who talk about parents while cutting the programs parents rely on. We get people who say they care about children but ignore childcare, afterschool care, tutoring, mental health support, transportation, and workforce preparation.

The cost of that failure is paid by families.

It is paid by the parent who cannot take a shift because school support was cut. It is paid by the student who graduates without a plan. It is paid by the small business owner who cannot find trained workers. It is paid by the teacher who leaves the profession because the workload is impossible and the respect is gone. It is paid by the community that watches young people drift away because there is no clear path from classroom to career.

The choice before voters is not abstract. It is not left versus right in the way political consultants like to frame it. It is whether we want leaders who understand that education is connected to work, wages, public safety, family stability, and local economic growth.

If we want schools that open doors instead of close them, we have to vote like it. We have to recruit leaders who see education as more than a line item. We have to support candidates who understand that a school is not just where children learn. It is where communities prepare for the future.

This is your neighbor's job. Your cousin's diploma. Your child's future. Your local business's next employee. Your community's next generation of nurses, electricians, teachers, engineers, entrepreneurs, and public servants.

So the next time someone tells you elections do not matter, ask them what happens when the school closes. Ask them what happens when the teacher shortage hits their child's classroom. Ask them what happens when the afterschool program disappears, the counselor is gone, the training program is cut, or the community college pipeline dries up.

Then remind them what real leadership is supposed to do.

CommonSense Democrats keep schools open, families working, and futures possible.

Know someone who gets it? Nominate a community leader who should run for office at CommonSenseDemocrats.us. The best way to protect our schools and our future is to help the right people lead.`,
      author: "CommonSense Democrats"
    },
    {
      id: 3,
      slug: "jobs-workforce-small-business",
      image: "/articles/jobs-workforce-small-business.jpg",
      title: "Why Aren't Our Leaders Putting Jobs, Workforce Development, and Small Business First?",
      excerpt: "In communities across the country, the message is loud and clear: people want to work, build, and grow. They want their children to have opportunities to succeed without having to leave their hometowns...",
      content: `In communities across the country, the message is loud and clear: people want to work, build, and grow. They want their children to have opportunities to succeed without having to leave their hometowns. They want small businesses to thrive, not merely survive.

Yet these everyday priorities continue to receive far less attention than they deserve in local, state, and federal policymaking.

Jobs, workforce development, and small business support are the foundation of a strong economy. They determine whether families can build financial security, whether communities can attract investment, and whether the next generation sees a future where they live. These are not partisan issues. They are practical issues that affect every American. So why are so many current elected officials failing to make these priorities central to their agenda?

Too often, public debate is dominated by political theater, partisan conflict, and issues designed to generate headlines rather than meaningful progress. While elected officials argue, employers struggle to find skilled workers, small businesses face rising costs, and communities lose economic opportunities.

We need leaders who are focused on results instead of rhetoric.

Candidates who prioritize job creation, workforce training, apprenticeships, career and technical education, support for entrepreneurs, and partnerships between employers, schools, and local governments understand that long term prosperity is built through opportunity.

Strong workforce development means preparing people for the jobs that exist today while anticipating the industries of tomorrow. It means investing in community colleges, vocational education, apprenticeships, and skills training that align with the needs of local employers. These investments strengthen businesses while providing workers with pathways to higher wages and lasting careers.

Small businesses deserve the same attention. They create jobs, revitalize neighborhoods, and drive innovation. Yet many entrepreneurs continue to face unnecessary regulatory burdens, limited access to capital, workforce shortages, and rising operating costs. Supporting small businesses is one of the most effective ways to strengthen local economies and create sustainable growth.

CommonSense Democrats believes voters deserve candidates who are committed to practical economic solutions. We support leaders who understand that economic growth begins with people, businesses, and communities, not political talking points. These are candidates willing to work across differences to expand opportunity, strengthen the workforce, and create an environment where businesses of every size can succeed.

If you know a leader in your community who believes in practical problem solving, economic opportunity, and responsible leadership, we encourage you to nominate them. CommonSense Democrats is building a movement focused on common sense solutions, stronger communities, and leaders who put results ahead of politics.

America's future depends on leaders who are willing to invest in jobs, workforce development, and small business growth. It is time to ask more of those who represent us and support candidates who are ready to deliver real solutions for the communities they serve.

CommonSense Democrats is actively seeking pragmatic, solutions focused candidates for local, state, and federal office. If you know someone who is ready to lead with common sense and put economic opportunity first, nominate them today and help build a stronger future for every community.`,
      author: "CommonSense Democrats"
    },
    {
      id: 4,
      slug: "midterms-stress-test",
      image: "/articles/midterms-stress-test.jpg",
      title: "The Midterms Will Be a Stress Test for the Country",
      excerpt: "You can feel it. Confidence in Congress is thinning out. Voters are tired of performance politics...",
      content: `You can feel it.

Confidence in Congress is thinning out. Voters are tired of performance politics. The loudest voices get the most airtime, yet the issues that actually shape daily life sit unresolved. Predicting that this trajectory leads to electoral consequences is not dramatic. It is structural.

Midterms are rarely about ideology alone. They are about pressure. And right now, families are under pressure.

There is a segment of the electorate, roughly 15 percent, that will decide more races than pundits want to admit. They are not hyper-partisan. They are not driven by cable news outrage cycles. They are pragmatic. Their focus is narrow and concrete:

• The economy and the cost of living
• Public safety in their neighborhoods
• Education outcomes for their children
• Health care affordability

If candidates cannot speak credibly to those four pillars, they will struggle. If they can, they win.

The opportunity is clear. That 15 percent is persuadable. They respond to seriousness. They respond to solutions. They respond to leaders who acknowledge trade-offs instead of pretending government is magic.

The danger is equally clear. If this bloc disengages, turnout drops. When turnout drops, extremes dominate. When extremes dominate, Congress becomes even less functional. It becomes a self-reinforcing loop of noise without results.

So the question is not whether the midterms matter. They do. The question is whether we will recruit and elevate candidates capable of activating this decisive segment.

CommonSense Democrats was built for precisely this moment. The mission is straightforward: identify and support leaders who are grounded, competent, and focused on tangible outcomes. People who understand budgets. People who value public safety and civil rights at the same time. People who can talk about economic growth without ignoring cost pressures. People who treat education and health care as systems that must deliver results, not slogans.

Winning in this environment will not come from viral moments. It will come from disciplined messaging, credible leadership, and a coalition that includes independents, moderates, and practical Democrats who still believe government can function.

Now we need your help.

If you know someone who fits this profile, nominate them. A mayor. A state legislator. A community leader. A veteran. A small business owner. A school board member who has actually balanced a budget and improved outcomes. The next wave of effective leadership is already in our communities. It just needs a platform.

Submit your nomination to CommonSense Democrats and help us build the slate that can win where it matters most. The midterms will be critical. Let's make sure we have the right people on the ballot when they arrive.`,
      author: "CommonSense Democrats"
    },
    {
      id: 5,
      slug: "housing-crisis",
      image: "/articles/housing-crisis.jpg",
      title: "The Housing Crisis Isn't Coming. It's Already Here",
      excerpt: "The American Dream used to include a home, a little stability, and the belief that hard work could turn today's paycheck into tomorrow's security. For too many Americans, that dream now looks like five roommates, a rent increase, or a down payment that moves further out of reach every year...",
      content: `The American Dream used to include a home, a little stability, and the belief that hard work could turn today's paycheck into tomorrow's security. For too many Americans, that dream now looks like five roommates, a rent increase, a two-hour commute, or a down payment that moves further out of reach every year.

This is not a future problem. It is already here.

Across the country, housing costs have outpaced what families can reasonably afford. The problem is most visible in major cities, but it is no longer confined to the places people once expected to be expensive. Smaller towns, suburbs, rural communities, and working-class neighborhoods are feeling the pressure too. The crisis has spread because the basic math has broken. Wages have not kept up with housing costs, and the supply of homes people can actually afford has not kept up with demand.

The result is an economy where the people who keep communities running often cannot afford to live in those same communities.

Teachers, nurses, firefighters, police officers, restaurant workers, construction workers, public employees, home health aides, and young professionals are being priced out of the places that depend on their labor. A community cannot function when the people who educate its children, care for its patients, protect its streets, and staff its businesses have to live an hour or two away just to make the rent work.

That is not just unfair. It is economically dangerous.

Housing is not separate from the rest of life. It determines whether a parent can get to work on time. It determines whether a child stays in the same school. It determines whether a small business can hire enough workers. It determines whether a young family can put down roots, whether a senior can age in place, and whether a community remains stable across generations. When housing fails, everything else gets harder.

Young families are making brutal calculations. Do they have children, or do they save for a down payment? Do they stay near aging parents, or move three states away to find rent they can afford? Do they take the better job, or avoid it because the commute would swallow their life? Do they stay in the neighborhood they love, or leave because the next lease renewal makes staying impossible?

These are not personal failures. They are policy failures.

Housing did not become unaffordable by accident. It became unaffordable through years of decisions that restricted supply, delayed construction, discouraged density, underbuilt starter homes, and treated housing like a political problem instead of economic infrastructure. When local governments slow-walk approvals, costs rise. When zoning laws make it illegal or nearly impossible to build anything other than single-family homes in high-demand areas, supply stays frozen. When permitting takes years, financing gets more expensive, projects die, and families pay the price.

At the same time, too many communities have failed to protect renters from predatory practices while also failing to build enough housing for working families. That combination is devastating. Renters are squeezed by rising costs. First-time buyers are blocked by high prices and high interest rates. Seniors on fixed incomes are pressured by property taxes, insurance, and maintenance costs. Essential workers end up commuting farther. Young adults delay marriage, children, and homeownership. Local economies lose talent.

This is the ripple effect of treating housing as somebody else's problem.

Some politicians talk about affordability as if saying the word is enough. Others reduce housing to a fight between renters and homeowners, developers and neighborhoods, growth and preservation. That kind of politics misses the point. A serious housing agenda has to do more than pick a side. It has to solve the actual shortage, protect people from abuse, respect community character, and create pathways for working families to live near opportunity.

That requires common sense leadership.

Communities need leaders who can streamline housing approvals without sacrificing quality or safety. They need leaders who support mixed-income development, workforce housing, starter homes, accessory dwelling units, and responsible density near jobs and transit. They need leaders who understand that renter protections and property rights are not mutually exclusive. They need leaders who will invest in public transit so people can live affordably and still reach jobs, schools, health care, and small businesses. They need leaders who can work with local builders, unions, nonprofits, employers, and community groups to expand supply responsibly.

None of this is easy. But it is necessary.

The worst approach is pretending that doing nothing is neutral. Doing nothing is a choice. It means higher rents, longer commutes, fewer young families, more displacement, more homelessness, weaker schools, and businesses that cannot find workers. It means a teacher leaves the district because she cannot afford to live there. It means a firefighter sleeps in his car between shifts. It means a retiree sells the home he lived in for decades because the cost of staying became too high. It means the next generation looks at the community that raised them and concludes there is no place for them there.

That is a failure of leadership.

CommonSense Democrats believe housing must be treated as the economic priority it is. Housing is not a luxury. It is the foundation that makes education, employment, family stability, public safety, and local growth possible. A community without housing people can afford is a community choosing decline, even if it does not admit it yet.

The next generation is watching. They are asking a simple question: will leaders help us build lives here, or will they force us to leave?

That question should be at the center of every local election, every state legislative race, and every congressional campaign. Housing policy is not just about buildings. It is about whether communities remain livable for the people who make them work.

The housing crisis will not solve itself. It will take leaders with courage, discipline, and the ability to move past slogans. It will take leaders who understand that smart development, tenant protections, workforce housing, and local economic growth all belong in the same conversation.

If you know a local leader who understands this, someone who has fought for smart development, fair housing, responsible growth, tenant protections, or workforce housing, nominate them to run for office at CommonSenseDemocrats.us.

The housing crisis is already here. The right leaders can still solve it.`,
      author: "CommonSense Democrats"
    },
    {
      id: 6,
      slug: "public-safety",
      image: "/articles/public-safety.jpg",
      title: "Public Safety Isn't a Slogan. It's a Daily Reality",
      excerpt: "Public safety has become one of the most abused phrases in American politics. Meanwhile, most Americans are not living inside those slogans. They are living real lives...",
      content: `Public safety has become one of the most abused phrases in American politics. One side uses it to demand punishment without reform. Another uses reform language without always acknowledging the fear people feel when crime hits their block, their school, their business, or their family.

Meanwhile, most Americans are not living inside those slogans. They are living real lives.

They want their children to walk to school safely. They want their parents to sit on the porch without fear. They want small businesses to stay open after dark. They want police to respond when someone calls for help. They want officers who are trained, professional, accountable, and respected. They want violent crime reduced. They want civil rights protected. They want mental health crises handled by people prepared to handle them.

Those are not contradictory demands. They are common sense.

A grandmother in Philadelphia may want drug dealers off her block and still want her grandson treated with dignity by the police. A small business owner in Phoenix may want faster response times and also want a trained mental health team responding to the man in crisis outside his storefront. A parent in Baltimore may want more patrols near school dismissal and also want youth programs that keep teenagers from being pulled into violence in the first place.

That is where the current political debate fails. It forces people into a false choice. You are told to be either pro-police or pro-reform, either tough on crime or serious about fairness, either concerned about victims or concerned about rights. But most people do not live that way. Most people want effective law enforcement and accountability. They want prevention and consequences. They want compassion and order. They want safety and fairness, because they know one without the other does not last.

CommonSense Democrats refuse the false choice.

Public safety is not about proving which side can shout louder. It is about whether the strategy works. It is about whether emergency calls are answered quickly, whether serious offenders are held accountable, whether neighborhoods most affected by violence receive real support, and whether public trust is strong enough for residents to cooperate with law enforcement when something goes wrong.

Trust matters because public safety is not created by police alone. It is created by a system that includes law enforcement, courts, schools, housing, mental health care, addiction treatment, street outreach, employment, community organizations, and families. When any part of that system breaks, safety becomes harder to maintain.

That does not mean policing is unimportant. It means policing has to be part of a broader strategy.

Communities need well-trained, well-managed, properly staffed law enforcement agencies that can respond to emergencies, investigate crimes, protect victims, and remove dangerous people from the street. They also need accountability systems that protect good officers and remove bad ones, because misconduct weakens public trust and makes everyone less safe. A department that lacks discipline loses legitimacy. A department that lacks resources loses effectiveness. Both failures hurt the public.

At the same time, not every public safety problem can be solved with an armed response. Mental health crises, addiction, homelessness, domestic instability, youth violence, and neighborhood disorder often require specialized tools. Sending police to every crisis by default places too much burden on officers and too little responsibility on the systems that should be working alongside them.

That is why serious public safety leadership must include mental health crisis response teams, violence interruption programs, addiction treatment, reentry support, youth programs, and job pathways. These are not soft alternatives to safety. When done well, they are part of safety. They help prevent the next emergency call, the next shooting, the next overdose, the next arrest, and the next family tragedy.

The communities suffering most from crime understand this better than anyone. They are not asking for academic theories or cable news arguments. They are asking for peace on their streets. They are asking for a government that can protect them without violating them. They are asking for leaders who know the difference between being loud and being effective.

Public safety is also an economic issue. When people do not feel safe, businesses close earlier, workers leave jobs, families move away, students lose focus, and neighborhoods lose investment. Violence drains local economies. Disorder weakens public confidence. Slow emergency response makes people feel abandoned. A city cannot grow if its residents do not believe basic safety is possible.

That is why slogans are not enough.

"Tough on crime" means very little if cases are not solved, witnesses do not trust the system, officers are burned out, and prevention is ignored. "Reform" means very little if families feel unsafe, businesses are left exposed, and violent offenders face no meaningful accountability. Real public safety requires competence, discipline, resources, transparency, and strategy.

CommonSense Democrats support leaders who can hold more than one truth at the same time.

We need leaders who will invest in law enforcement and community-based violence prevention. Leaders who will support mental health crisis response alongside traditional policing. Leaders who will demand accountability without treating every officer as the enemy. Leaders who will address poverty, addiction, trauma, and lack of opportunity without pretending that root causes excuse violence. Leaders who will use data to measure what actually reduces crime instead of chasing whatever message polls well that week.

This is the balance voters are looking for.

They do not want elected officials who use public safety to score points. They do not want leaders who disappear until a tragedy creates a press conference. They do not want ideology dressed up as policy. They want people capable of doing the job.

Public safety is not a culture war. It is a responsibility. It is the daily work of making sure a child can get to school, a senior can walk to the store, a business can keep its doors open, a victim can get justice, and a community can trust the people sworn to protect it.

That is the kind of leadership CommonSense Democrats support: practical, balanced, accountable, and focused on results.

If you know a leader who understands this balance, whether it is a police chief, a community organizer, a city council member, a prosecutor, a victim advocate, a social worker, or a neighborhood leader, nominate them to run for office at CommonSenseDemocrats.us.

Public safety is not a slogan. It is a job. Let's elect people capable of doing it.`,
      author: "CommonSense Democrats"
    },
    {
      id: 7,
      slug: "democratic-party-breaking-apart",
      image: "/articles/democratic-party-breaking-apart.jpg",
      title: "The Democratic Party Is Quietly Breaking Apart From Within",
      excerpt: "Over the last several weeks, Democratic primary elections across the country have revealed something party leadership should not ignore...",
      content: `Over the last several weeks, Democratic primary elections across the country have revealed something party leadership should not ignore. From New York to Colorado, and increasingly in local races across major urban centers, a new generation of candidates is reshaping what Democratic political power looks like and how legitimacy inside the party is earned.

At first glance, these outcomes may appear disconnected. A surprising primary upset here. A grassroots candidate outperforming expectations there. But taken together, these races suggest something deeper unfolding inside the Democratic Party itself. The assumptions that have governed Democratic politics for decades are beginning to weaken, and a new political order is beginning to emerge.

To understand this moment, it is important to examine the candidates and movements driving this shift.

In New York, Zohran Mamdani has become one of the clearest symbols of an emerging political infrastructure operating outside traditional Democratic power centers. His rise demonstrates that candidates no longer need to rely exclusively on legacy donor networks, institutional endorsements, or long established party relationships to build viable political power. Campaigns rooted in grassroots organizing, digital communication, and direct voter mobilization are proving increasingly competitive.

The ecosystem developing around candidates like Mamdani is equally important. Figures such as New York State Representative Claire Valdez (Democratic Nominee for NY-7 Congressional District), New York City Comptroller Brad Lander (Democratic Nominee for NY-10 Congressional District), and Activist Darializa Avila Chevalier (Democratic Nominee for NY-13 Congressional District) represent a broader pipeline of Democratic candidates building credibility through community engagement, organizing infrastructure, and coalition building rather than relying solely on traditional political pathways.

In Washington DC, Janeese Lewis George offered an earlier signal of this same shift. Her political rise reflected growing voter demand for leaders perceived as deeply connected to neighborhood concerns rather than candidates elevated primarily through institutional machinery. Subsequently, we're learning that accessibility and authenticity are becoming political assets equal to experience and seniority.

Then came perhaps the clearest signal yet in Colorado, where 29 year old Melat Kiros defeated longtime incumbent Diana DeGette, ending nearly three decades of congressional incumbency. The significance of that victory extends far beyond a single race. It challenged one of the Democratic Party's oldest operating assumptions that incumbency, fundraising strength, and institutional support are enough to guarantee political security.

Taken together, these developments reveal a consistent pattern. Voters are increasingly rewarding candidates who feel culturally aligned with present day realities and less connected to institutional politics. The traditional advantages that once protected long standing political figures are no longer functioning with the same reliability they once did.

For decades, Democratic politics has operated through a relatively stable internal ecosystem. Incumbency created security. Party endorsements reinforced fundraising. Institutional relationships protected political longevity. That formula now appears increasingly vulnerable. However, this shift may not be purely ideological, especially given, the too-often interpretation, that these victories are evidence that Democratic voters simply want the party to move further left. That conclusion may be incomplete.

A more important question remains unanswered: Are voters embracing progressive ideology itself, or are they responding to something broader, like authenticity, urgency, cultural relevance, and a desire for candidates who communicate as if they genuinely understand the economic anxiety defining modern American life?

That distinction matters enormously and if this moment is primarily ideological, these victories may remain concentrated in safely Democratic districts and progressive urban centers. However, if voters are responding to communication style, generational identity, and candidates perceived as more connected to lived experience, the implications extend far beyond isolated local races.

That becomes particularly important in battleground states such as Pennsylvania, Michigan, Wisconsin, Georgia, Arizona, and Nevada, where elections are won not through ideological purity but through broad coalition politics. Candidates must simultaneously motivate younger voters while maintaining support among moderates, union households, independents, suburban voters, and persuadable working class communities, which is where the Democratic Party faces a genuine strategic dilemma.

For years, Democratic messaging has centered around policy expertise, institutional competence, and protecting democratic norms. Those priorities remain important, but younger voters appear motivated by something different. They are rewarding candidates who project urgency, emotional clarity, economic empathy, and a level of directness often absent from traditional party communication.

What makes this moment even more significant is that many of these campaigns share a common structural advantage. Across many of these races, candidates have benefited from organizing models associated with groups like Democratic Socialists of America, organizations that have consistently invested in grassroots candidate recruitment, volunteer mobilization, and message discipline over multiple election cycles.

This should not be dismissed as simply progressive enthusiasm. It actually represents a growing organizing infrastructure operating inside the broader Democratic ecosystem, one capable of identifying voter frustration and converting that frustration into measurable political power.

The Democratic Party now faces a choice: It can dismiss these victories as isolated disruptions confined to progressive strongholds, or it can seriously examine what these campaigns reveal about how a growing segment of the electorate now engages with politics. The lesson here may not be ideological, but more so organizational.

These implications may soon reach presidential politics and the next Democratic presidential nominee will almost certainly face a coalition that is growing divided between traditional institutional Democrats and a younger electorate shaped by grassroots political movements, economic populism, and candidates operating outside conventional party structures. That tension will likely influence everything from primary endorsements and campaign messaging to turnout strategy and coalition management in a national election.

If party leadership misreads this moment, Democrats risk entering future presidential races with a political playbook designed for an electorate that is rapidly changing beneath them. What we are witnessing is not simply a series of isolated primary victories. It is the early stage of a fundamental reorganization of power inside the Democratic Party itself. And, whichever faction learns to understand this shift first may very well determine the future direction of American presidential politics for the next generation.`,
      author: "CommonSense Democrats"
    },
    {
      id: 8,
      slug: "generational-change",
      image: "/articles/generational-change.jpg",
      title: "The Case for Generational Change in American Political Leadership",
      excerpt: "Across the United States, trust in political institutions is waning, polarization is deepening, and voters, especially younger ones, are disengaging from a system that feels stagnant and unresponsive...",
      content: `Across the United States, trust in political institutions is waning, polarization is deepening, and voters, especially younger ones, are disengaging from a system that feels stagnant and unresponsive. A common thread runs through these challenges: an aging political leadership class that too often governs with outdated assumptions, entrenched priorities, and a reluctance to embrace transformational change. It is time for a generational shift across both major political parties.

This is not about dismissing experience or devaluing wisdom. It is about acknowledging that leadership must evolve with the times. America's challenges, from climate change to artificial intelligence, from mental health to the erosion of democratic norms, are not best understood solely through the lens of past decades. Millennials and Generation Z bring different lived experiences shaped by economic uncertainty, technological disruption, and rapid social change.

At every level of government, power remains concentrated among leaders who entered politics in a very different America. Too often, institutional incentives reward seniority over innovation, caution over creativity, and maintaining the status quo over solving emerging problems. The result is policy gridlock, declining public trust, and an inability to respond quickly to new challenges.

A generational shift would bring more than new faces. It would introduce new perspectives, different methods of organizing, and a stronger connection to the realities many Americans experience today. Younger leaders tend to embrace collaboration, technology, transparency, and direct engagement with constituents. They are also more likely to approach issues with fewer ideological assumptions inherited from previous political eras.

This argument applies equally to Democrats and Republicans. Neither party benefits when leadership opportunities become concentrated within a small group of long serving political figures. Healthy organizations renew themselves by developing new talent, encouraging competition, and creating clear pathways for future leaders to emerge.

Generational change does not require pushing experienced leaders aside. Their institutional knowledge remains valuable. Instead, it calls for a deliberate transfer of responsibility, mentorship, and shared leadership that prepares the next generation to govern effectively while preserving lessons from the past.

America has always been at its strongest when each generation builds upon the work of those before it. The challenges of the coming decades will demand fresh thinking, technological fluency, and leaders who understand the realities facing today's voters. Renewing political leadership is not about age. It is about ensuring that our institutions continue to reflect the people they serve and remain capable of meeting the future with confidence.`,
      author: "CommonSense Democrats"
    },
    {
      id: 9,
      slug: "medical-marijuana-texas",
      image: "/articles/medical-marijuana-texas.jpg",
      title: "The Uphill Battle for Medical Marijuana Patients in Texas",
      excerpt: "For many Americans living in Texas, accessing medical marijuana is not simply a policy debate. It is a daily struggle against restrictive laws, limited access, high costs, and lingering stigma...",
      content: `For many Americans living in Texas, accessing medical marijuana is not simply a policy debate. It is a daily struggle against restrictive laws, limited access, high costs, and lingering stigma. While much of the country has expanded access to cannabis for medical use, Texas continues to maintain one of the nation's most restrictive programs, leaving many patients without treatment options that could improve their quality of life.

A Restrictive Program

Texas does have a medical marijuana program, known as the Compassionate Use Program (CUP), but it remains among the most limited in the United States. Only low THC cannabis products are permitted, and the list of qualifying medical conditions remains relatively narrow. Patients suffering from chronic pain, severe anxiety, glaucoma, and many other debilitating conditions often do not qualify, even when their physicians believe cannabis could provide meaningful relief.

Limited Access and High Costs

Even patients who qualify face significant barriers. Texas has only a small number of licensed dispensaries, forcing many residents, particularly those in rural communities, to travel long distances or rely on delivery services. Because medical cannabis is not covered by insurance, patients must also pay out of pocket for physician consultations and medication, creating a financial burden that many families simply cannot afford.

Fear and Stigma

Despite growing public support for medical marijuana, many Texans remain reluctant to discuss their treatment openly. Patients worry about being judged by employers, family members, or their communities. Confusing state laws also create uncertainty, particularly for patients who travel to neighboring states where broader medical cannabis programs exist.

The Politics of Inaction

Public opinion polls consistently show strong bipartisan support for expanding medical marijuana access in Texas. However, legislative reforms have advanced slowly. As a result, many patients continue to wait while lawmakers debate changes that could significantly improve their quality of life.

The Human Cost

Behind every policy debate are real people. Veterans managing PTSD, cancer patients coping with treatment side effects, children living with severe neurological disorders, and individuals experiencing chronic pain often find themselves with limited legal options. Some relocate to other states, while others continue to suffer without adequate treatment.

Looking Forward

Advocates, physicians, veterans, patients, and families continue to push for a more compassionate medical marijuana program in Texas. Expanding access would provide physicians with more treatment options and give patients the opportunity to pursue therapies that may improve their health and well being. As the conversation evolves, many Texans hope lawmakers will adopt policies that place patient care at the center of the discussion.`,
      author: "CommonSense Democrats"
    },
    {
      id: 10,
      slug: "voting-rights-act",
      image: "/articles/voting-rights-act.jpg",
      title: "The Court Weakened the Voting Rights Act. Black Voters Will Pay the Price.",
      excerpt: "The Supreme Court's decision in Louisiana v. Callais is a dangerous setback for voting rights, fair representation, and the promise of equal participation in American democracy. By weakening Section 2 of the Voting Rights Act, the Court has made it harder for communities of color to challenge maps that dilute their voting power.",
      content: `The Supreme Court's decision in Louisiana v. Callais is a dangerous setback for voting rights, fair representation, and the promise of equal participation in American democracy. By weakening Section 2 of the Voting Rights Act, the Court has made it harder for communities of color to challenge maps that dilute their voting power. The practical effect is clear: states now have more room to dismantle majority-minority districts, split communities apart, and defend racially harmful maps by calling them partisan strategy. That is not justice. That is disenfranchisement by another name.

CommonSense Democrats oppose this decision because it threatens voters of color not only in Louisiana, but across the country. The Voting Rights Act was created because America has a long history of using district lines, procedural barriers, and political manipulation to silence Black, Latino, Asian American, Native American, and other communities of color. That history is not ancient. Its effects are still present in the way political power is drawn, protected, and denied.

We agree with Vice President Kamala Harris's warning that this ruling allows racism to be back-doored through politics. When courts treat partisan gerrymandering as legally acceptable, even when it predictably strips communities of color of fair representation, they invite politicians to accomplish indirectly what the Voting Rights Act was designed to prevent directly.

This moment demands clarity and action. Congress must restore and strengthen the Voting Rights Act. Democrats must take Supreme Court reform seriously, including structural reforms that restore public trust in the Court. And voters must understand that the answer to disenfranchisement is not despair. It is organization, turnout, litigation, legislation, and the election of leaders who will defend democracy without hesitation. The right to vote is not a technicality. It is the foundation of self-government. When communities are denied fair representation, democracy itself is weakened.

CommonSense Democrats will continue to support candidates who defend voting rights, oppose racial vote dilution, and believe that every community deserves a fair chance to choose leaders who reflect their voices, their needs, and their future.`,
      author: "CommonSense Democrats"
    },
    {
      id: 11,
      slug: "ballot-on-time",
      image: "/articles/ballot-on-time.jpg",
      title: "The Supreme Court Got This One Right: A Ballot Cast on Time Should Count",
      excerpt: "Today's Supreme Court decision in Watson v. Republican National Committee is a victory for voters, for common sense, and for the basic principle that lawful ballots should not be thrown out because the mail moves slower than democracy.",
      content: `Today's Supreme Court decision in Watson v. Republican National Committee is a victory for voters, for common sense, and for the basic principle that lawful ballots should not be thrown out because the mail moves slower than democracy. CommonSense Democrats agrees with Justice Barrett's majority opinion and strongly disagrees with Justice Alito's dissent.

The question before the Court was narrow but important: can Mississippi count absentee ballots that are postmarked by Election Day but received up to five business days later? The Court answered correctly. Federal law sets the deadline for when voters must make their choice. It does not create a national rule requiring every ballot to physically arrive in an election office by Election Day. That distinction matters.

A voter who fills out a ballot, signs it, and places it in the mail by Election Day has voted on time. That voter has done what the law requires. The government should not discard that vote because of mail delays, rural delivery issues, military service, disability, age, school, work, or distance from home.

Justice Barrett's opinion recognizes that reality. She makes clear that the "election" is the voter's choice, not the backend administrative act of when an election office receives the envelope. The Court also recognized that federal law already treats ballot receipt deadlines as a matter states may regulate, especially under laws protecting military and overseas voters.

Justice Alito's dissent takes the opposite approach. It treats late-arriving ballots as inherently suspect and leans heavily on fear of fraud, even when the ballots were cast on time under state law. That theory would punish voters for circumstances they do not control. It would also invite courts to override state election rules without clear authorization from Congress. That is not election integrity. That is voter disenfranchisement dressed up as suspicion.

Real election integrity means clear rules, secure processes, and counting every lawful vote. It does not mean changing the rules after voters have complied with them. It does not mean throwing out ballots from seniors, students, rural voters, disabled voters, military families, or citizens living away from home because a delivery system takes a few extra days.

Roughly 30 states count at least some absentee ballots that are mailed by Election Day but received afterward, and the Supreme Court's ruling protects those voters from a sweeping interpretation that could have disrupted elections across the country. The 5 to 4 decision preserves similar laws in about 30 states and the District of Columbia.

The Court's majority also respected the role of democracy itself. If Congress wants to create a national ballot receipt deadline, Congress can do that. But courts should not invent one. As Justice Barrett put it, the election-day statutes say nothing about ballot receipt, and courts cannot add words Congress did not choose. That is the right principle.

CommonSense Democrats believes voters should decide elections, not technical traps, partisan lawsuits, or speculative theories designed to discard ballots that were legally cast. The strength of our democracy depends on whether citizens believe their vote will count when they follow the rules. Today, the Supreme Court protected that promise. A ballot cast on time should count. Period.`,
      author: "CommonSense Democrats"
    },
    {
      id: 12,
      slug: "birthright-citizenship",
      image: "/articles/birthright-citizenship.jpg",
      title: "Birthright Citizenship Is the Constitution's Promise, Not a Political Loophole",
      excerpt: "Trump's attack on birthright citizenship was not just an immigration fight. It was an attempt to revive the old idea that belonging in America depends on bloodline. The Supreme Court got this one right in Trump v. Barbara.",
      content: `Trump's attack on birthright citizenship was not just an immigration fight. It was an attempt to revive the old idea that belonging in America depends on bloodline.

The Supreme Court got this one right in Trump v. Barbara. The Court affirmed a principle that should never have been in doubt: children born in the United States are citizens of the United States. That is not a loophole, a political giveaway, or a favor granted by any president. It is a constitutional promise written into the Fourteenth Amendment. The Citizenship Clause is clear: "All persons born or naturalized in the United States, and subject to the jurisdiction thereof, are citizens of the United States."

Those words were adopted after the Civil War to reject one of the most shameful ideas in American history: the belief that some people could be born on American soil and still be denied full belonging under the law. President Trump's executive order attempted to deny citizenship to children based on the immigration status of their parents. The Court rejected that effort because the Constitution does not give presidents the power to rewrite citizenship by executive order. No president, Democrat or Republican, should have that authority.

CommonSense Democrats believes the Constitution matters most when it is inconvenient. It matters when the issue is controversial, when fear is being used as a political weapon, and when politicians try to turn vulnerable children into symbols for campaign rallies. It also matters when the plain meaning of our founding documents stands in the way of someone's agenda.

The contrast could not be clearer. One side tried to treat citizenship as a political tool, something that could be narrowed, redefined, or taken away depending on who is in power. The Court said no. The Fourteenth Amendment is not a suggestion, a campaign slogan, or a legal technicality. It is the law of the land.

We should also be honest about what is really happening. This fight is not only about immigration policy. It is about whether white supremacy is being allowed to creep back into mainstream American politics through sanitized language, legal theories, and executive power. Donald Trump and the MAGA movement have repeatedly tried to redefine who belongs in America. They may use words like "jurisdiction," "invasion," "illegals," or "national identity," but the underlying message is familiar: some people are treated as real Americans, while others are treated as outsiders no matter where they were born, how they live, or what the Constitution says.

White supremacy does not always return wearing a hood. Sometimes it returns wearing a suit, holding a legal memo, and claiming to defend the Constitution while gutting its most important guarantees. Sometimes it arrives as an executive order. Sometimes it appears as a theory that says a child born on American soil can still be denied citizenship because of who their parents are. That is precisely the kind of thinking the Fourteenth Amendment was designed to bury.

After the Civil War, this country amended the Constitution because the old order had tried to make citizenship a matter of bloodline, race, and inherited status. The Citizenship Clause was a direct rejection of that idea. It said that America would no longer allow politicians, courts, or racial hierarchies to decide that some people born here are lesser members of the national community.

Justice Ketanji Brown Jackson's concurrence captured the deeper meaning of this case. The Fourteenth Amendment was not written as a narrow repair job for one group alone. It was written to destroy caste, reject inherited status, and guarantee equal citizenship under law. Its promise is universal because the evil it answered was bigger than one moment in history.

That is the point too many politicians want Americans to miss. Birthright citizenship is not about rewarding unlawful immigration. It is about refusing to punish children for the status of their parents. It is about ensuring that America does not create a permanent underclass of children born here, raised here, educated here, and yet told they do not belong here. A country that believes in the Constitution cannot allow citizenship to depend on bloodline. A country that believes in equal justice cannot allow politicians to decide which babies count as American. A country that believes in democracy cannot allow executive power to override constitutional guarantees.

This ruling is a victory for the Constitution, for the rule of law, and for the basic American promise that birth on American soil carries meaning. CommonSense Democrats will always support serious border policy, lawful immigration reform, and responsible government. But we will not support efforts to distort the Constitution for political gain, and we will not accept the idea that some children born in America should begin life as constitutional outsiders.

This case forces a bigger question: are we going to be a constitutional democracy, or are we going to let a movement built on resentment decide who counts as American? CommonSense Democrats believes the answer must be clear. The Constitution does not belong to Donald Trump, MAGA, or any faction that wants to narrow the promise of America to fit a racial or political agenda. The Constitution belongs to all of us. It matters because it protects all of us from the politics of the moment. Today, the Supreme Court remembered that. So should the country.`,
      author: "CommonSense Democrats"
    },
    {
      id: 13,
      slug: "campaign-finance",
      image: "/articles/campaign-finance.jpg",
      title: "If Campaign Finance Rules Keep Getting Weaker, Why Have Campaign Finance Rules at All?",
      excerpt: "The Supreme Court's decision in National Republican Senatorial Committee v. Federal Election Commission raises a question bigger than one campaign finance statute: if the Court keeps weakening campaign finance rules, what purpose does campaign finance law still serve?",
      content: `The Supreme Court's decision in National Republican Senatorial Committee v. Federal Election Commission raises a question bigger than one campaign finance statute: if the Court keeps weakening campaign finance rules, what purpose does campaign finance law still serve? In a 6 to 3 ruling, the Court struck down federal limits on how much political parties can spend in direct coordination with their candidates. The majority held that those limits violate the First Amendment and overruled the Court's 2001 decision in Federal Election Commission v. Colorado Republican Federal Campaign Committee, which had upheld those restrictions.

CommonSense Democrats is not naive about money in politics. Campaigns cost money. Candidates need resources to hire staff, communicate with voters, organize communities, run ads, respond to attacks, and compete in a media environment where attention is expensive. A serious campaign cannot run on good intentions alone. But democracy also cannot survive if every campaign finance rule is treated as an unconstitutional burden and every dollar is treated as pure speech with no consequences attached.

Money is not just communication. Money creates expectations. In almost every part of human life, when someone gives something of value, they often expect something in return. That expectation may be direct or indirect, spoken or unspoken, immediate or delayed. In politics, that is exactly why limits matter. The danger is not always an envelope of cash or an explicit promise to trade a vote for a donation. Real influence is usually more subtle than that. It shows up in access, gratitude, priority, pressure, agenda-setting, and relationships that grow over time. A donor may not say, "I gave you this dollar, so I want this vote." But the expectation can still shape who gets a meeting, whose calls are returned, whose priorities are elevated, and whose concerns are treated as urgent.

That is why campaign finance law exists. It exists to prevent corruption. It exists to prevent wealthy donors from using indirect routes to get around contribution limits. It exists to preserve public trust in a system where voters need to believe elected officials are accountable to them, not just to the people and interests capable of writing the largest checks.

The Court's majority says existing contribution limits, earmarking rules, and disclosure requirements are enough to guard against corruption. According to the majority, federal limits on coordinated party spending are no longer necessary because the law already prohibits donors from routing money through a party when it is earmarked for a specific candidate. That argument may sound tidy in a courtroom. It is much less convincing in the real world. Disclosure is important, but disclosure is not the same as accountability. A campaign finance report may technically be public, but that does not mean ordinary voters can easily track the flow of money through party committees, joint fundraising operations, consultants, vendors, and coordinated campaign spending. Transparency matters, but transparency alone does not stop influence from being purchased. It often tells the public what happened only after the power has already been exercised.

The Court's decision also rests on a very narrow definition of corruption. Under the Court's modern campaign finance logic, the government has far less room to regulate money in politics unless it can point to something close to a direct exchange of money for an official act. But that is not how most political influence works. Influence is rarely that crude. It is often built through repeated giving, repeated access, repeated gratitude, and repeated dependence. If campaign finance law cannot recognize that reality, then the law becomes weaker than the problem it was created to solve.

Political parties should be able to support their candidates. That is not the issue. Parties organize voters, define policy choices, build coalitions, and help candidates compete. Strong parties can be healthier for democracy than a system dominated by outside groups and super PACs. But unlimited coordinated spending creates a different problem. It allows major donors to move money closer to candidates through party structures while still claiming the money is not going directly to the candidate. That weakens the purpose of contribution limits and makes it easier for wealth to become leverage. Justice Kagan's dissent warned that the ruling risks allowing parties to become financial extensions of individual campaigns, giving donors another path around candidate contribution limits. That concern should not be dismissed as partisan or theoretical. If a donor can give large sums to a party knowing the party can coordinate unlimited spending with a candidate, the line between party support and candidate support becomes thinner than ever.

So, we return to the central question: if campaign finance rules no longer limit the ways money can create political obligation, why have campaign finance rules at all? The answer cannot be that we simply document the money after the fact and call that democracy. The answer cannot be that access, influence, and gratitude are harmless unless prosecutors can prove an explicit deal. The answer cannot be that every limit is suspect, but every loophole is protected.

Campaign finance should not be anti-money. It should be anti-leverage. Candidates need money to win. Parties need money to compete. Voters need campaigns that can reach them with real information. But democracy also needs boundaries that prevent public office from becoming a return on investment. Congress should respond by strengthening disclosure, requiring faster reporting of large donations and coordinated expenditures, modernizing enforcement, and closing loopholes that allow donors to disguise influence through party structures or joint fundraising arrangements. If the Court is going to rely on disclosure and earmarking rules as the remaining safeguards, then those safeguards must be real, enforceable, and understandable to the public. The Supreme Court has made its ruling. But the moral question remains. A democracy can tolerate money in politics. It cannot tolerate a system where money becomes the measure of whose voice matters most.`,
      author: "CommonSense Democrats"
    },
    {
      id: 14,
      slug: "dc-taxes-hr9720",
      image: "/articles/dc-taxes-hr9720.jpg",
      featured: true,
      title: "Congress Should Keep Its Hands Off D.C.'s Tax Code",
      excerpt: "H.R. 9720 would give federal lawmakers a new veto over local fiscal decisions and further weaken the democratic rights of District residents.",
      content: `H.R. 9720 would give federal lawmakers a new veto over local fiscal decisions and further weaken the democratic rights of District residents.

The bill is broader than its title suggests. H.R. 9720 would prevent many changes to the District's tax code from taking effect unless both chambers of Congress pass a joint resolution of approval and the president signs it. The House Oversight Committee advanced the bill today, July 22, 2026. This reverses the current presumption of home rule by allowing congressional inaction alone to kill local legislation.

CommonSense Democrats unequivocally opposes this legislation.

Under this proposal, the elected government of Washington, D.C., could debate legislation, pass it, fund it and send it to Congress, only to have it die because federal lawmakers from across the country simply refused to act.

That is not responsible oversight. It is the deliberate obstruction of local democracy.

The people of Washington, D.C., pay federal and local taxes, serve in the military, operate businesses, raise families and contribute to the economic strength of this country. Yet members of Congress who do not live in the District, do not answer to its voters and will not experience the consequences of their decisions are attempting to take control of the city's most fundamental governing authority.

Supporters have presented this bill as a response to potential tax increases. The actual legislation reaches much further. It applies to acts involving Title 47 of the D.C. Code, potentially placing decisions concerning taxes, fees, exemptions, reductions and other fiscal policies at the mercy of Congress and the president.

Congress already possesses extraordinary authority over the District. H.R. 9720 would transform that authority into a permanent veto over local fiscal policy, weaken the ability of elected officials to manage the city's finances and create uncertainty for residents, businesses and public services.

CommonSense Democrats believes government should be accountable to the people most directly affected by its decisions. District residents elect a mayor and council to make difficult choices concerning revenue, spending and public services. Those officials should be judged by D.C. voters, not overruled by politicians from Kentucky, Texas, Florida or any other state.

We call on every member of the House and Senate to reject H.R. 9720 and defend the principle that taxation and representation must go together.

Washington, D.C., is a community, not a political laboratory. Its residents deserve the same dignity, democratic respect and right to self-government that Americans everywhere expect for themselves.`,
      author: "CommonSense Democrats",
      cta: {
        label: "Call Your Rep to Oppose H.R. 9720",
        url: "https://www.congress.gov/members/find-your-member",
        note: "Look up your House member and Senators, then call or write today. Your voice on this bill matters most before the full House votes."
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1569098644584-210bcd375b59?q=80&w=2070')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 to-slate-900/90"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 text-center">
          <img 
            src="/logo.png" 
            alt="CommonSense Democrats Logo" 
            className="w-64 md:w-80 lg:w-96 mx-auto mb-8 filter drop-shadow-2xl"
          />
          <h1 className="text-6xl md:text-7xl font-black text-white mb-6 leading-tight tracking-tight">
            LEADERS WITH<br />
            <span className="text-blue-400">COMMON SENSE</span>
          </h1>
          <p className="text-2xl md:text-3xl text-gray-200 mb-8 font-light">
            Building a coalition of pragmatic leaders who prioritize results over partisanship
          </p>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            We are Democrats, Independents, and responsible Republicans united by ethical governance, economic opportunity, and community-driven policies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
              onClick={() => window.open('https://secure.actblue.com/donate/commonsense-democrats---ie-1', '_blank')}
            >
              DONATE NOW
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
              onClick={() => document.getElementById('get-involved').scrollIntoView({ behavior: 'smooth' })}
            >
              GET INVOLVED
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              data-testid="hero-shop-merch-btn"
              className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
              onClick={() => window.open('https://commonsensedemocrats.printful.me', '_blank', 'noopener,noreferrer')}
            >
              <ShoppingBag className="w-5 h-5 mr-2" /> SHOP MERCH
            </Button>
          </div>

          {/* Social Media Links */}
          <div className="mt-12 flex gap-6 justify-center">
            <a 
              href="mailto:hello@commonsensedemocrats.us"
              className="text-gray-300 hover:text-blue-400 transition-colors duration-300"
            >
              <Mail className="w-8 h-8" />
            </a>
          </div>
        </div>
      </section>

      {/* Featured Event - A New Way to Democrat (day-of) */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-blue-400 text-sm font-bold uppercase tracking-widest mb-2">Congressional Black Caucus Foundation Annual Legislative Conference 2026</p>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-3">
              {isEventPast ? (
                <>Thank You, <span className="text-blue-400">Washington, D.C.</span></>
              ) : (
                <>Today in <span className="text-blue-400">Washington, D.C.</span></>
              )}
            </h2>
            <p className="text-lg text-gray-300">
              {isEventPast
                ? 'Grateful for the leaders, organizers, and neighbors who made September 18 possible.'
                : 'Doors open at 3:00 PM. The next chapter of our party starts this afternoon.'}
            </p>
          </div>

          <div className="grid gap-8 max-w-4xl mx-auto">

            {/* VIP Leadership Reception Card */}
            {(() => { const vip = events.find(e => e.kind === 'VIP'); if (!vip) return null; return (
            <Dialog key={vip.slug} open={openModal === vip.slug} onOpenChange={(o) => handleModalChange(vip.slug, o)}>
              {/* VIP card hidden on homepage — dialog remains reachable via #event-vip-reception hash */}
              <DialogContent
                className="max-w-6xl w-[95vw] max-h-[95vh] bg-slate-900 border-pink-500/40 text-white p-0 overflow-hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
              >
                <DialogTitle className="sr-only">{vip.title}</DialogTitle>
                <ScrollArea className="max-h-[90vh]">
                  <div data-testid="event-vip-modal">
                    <div className="relative bg-slate-900">
                      <img src={vip.image} alt={vip.title} className="w-full h-auto" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900 from-60% to-transparent pointer-events-none" />
                    </div>
                    <div className="p-6 md:p-10">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="text-pink-400 text-sm font-bold uppercase tracking-widest">{vip.eyebrow}</span>
                        <span className="inline-block px-2 py-0.5 rounded-full bg-pink-500 text-white text-[10px] font-bold uppercase tracking-widest">
                          {vip.accessBadge}
                        </span>
                      </div>
                      <h3 className="text-3xl md:text-4xl font-black text-white leading-tight mb-3">{vip.title}</h3>
                      <p className="text-lg text-gray-300 italic mb-6 leading-relaxed">{vip.tagline}</p>

                      <div className="border-l-2 border-pink-500 pl-4 mb-5">
                        <p className="text-xs uppercase tracking-widest text-pink-400 font-semibold mb-1">Hosted By</p>
                        {vip.hostedBy.map((h, i) => (
                          <div key={i} className={i > 0 ? 'mt-2' : ''}>
                            <p className="text-white font-semibold text-lg">{h.name}</p>
                            {h.role && <p className="text-sm text-gray-400">{h.role}</p>}
                          </div>
                        ))}
                      </div>

                      <div className="border-l-2 border-pink-500/50 pl-4 mb-6">
                        <p className="text-xs uppercase tracking-widest text-pink-400 font-semibold mb-1">Featuring</p>
                        <p className="text-white font-bold text-lg">{vip.featuredGuest}</p>
                        {vip.guestTitle.map((t, i) => <p key={i} className="text-sm text-gray-400">{t}</p>)}
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3 mb-6 text-sm">
                        <div className="flex items-start gap-2 text-gray-300">
                          <Calendar className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Date</p>
                            <p>{vip.date}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 text-gray-300">
                          <Clock className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Time</p>
                            <p>{vip.time}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 text-gray-300 sm:col-span-2">
                          <MapPin className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Venue</p>
                            <p className="font-semibold text-white">{vip.venue}</p>
                            <p className="text-sm text-gray-400 whitespace-pre-line">{vip.address}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 text-gray-300 leading-relaxed mb-6">
                        {vip.description.map((p, i) => <p key={i}>{p}</p>)}
                      </div>

                      <div className="rounded-lg bg-pink-950/40 border border-pink-500/30 p-5 mb-6">
                        <p className="text-xs uppercase tracking-widest text-pink-400 font-semibold mb-3">Attendance</p>
                        <p className="text-white font-bold uppercase tracking-wider text-sm mb-1">RSVP Required</p>
                        <p className="text-white font-bold uppercase tracking-wider text-sm mb-3">Invitation Only</p>
                        <p className="text-sm text-gray-300 leading-relaxed mb-4">{vip.attendanceNote}</p>
                        <p className="text-xs uppercase tracking-widest text-pink-400 font-semibold mb-2">Suggested Contribution Levels</p>
                        <div className="flex flex-wrap gap-2">
                          {vip.contributionLevels.map((amt, i) => (
                            <span key={i} className="inline-block px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/40 text-white font-bold">
                              {amt}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
                        {vip.rsvpUrl && (
                          <Button
                            data-testid="event-vip-modal-rsvp"
                            variant="outline"
                            className="w-full sm:w-auto border-2 border-pink-400 text-pink-400 hover:bg-pink-500 hover:text-white font-bold uppercase tracking-wider px-8 py-6 text-sm"
                            onClick={() => window.open(vip.rsvpUrl, '_blank', 'noopener,noreferrer')}
                          >
                            RSVP for the VIP Reception
                          </Button>
                        )}
                      </div>

                      {renderEndorsementShare(vip.slug, vip.title)}
                    </div>
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
            ); })()}

            {/* Public Event Card - Angie Nixon (A New Way to Democrat) - Day-of layout */}
            {/* Hidden after the event ends; the modal is still reachable via the "View Full Details" links inside the event routes for old share URLs. */}
            {!isEventPast && (() => { const pub = events.find(e => e.kind === 'PUBLIC'); if (!pub) return null; return (
            <Dialog key={pub.slug} open={openModal === pub.slug} onOpenChange={(o) => handleModalChange(pub.slug, o)}>
              <div
                data-testid="event-card-public"
                className={`relative rounded-2xl border-2 ${isEventPast ? 'border-blue-500/40' : 'border-blue-400/80 ring-1 ring-blue-400/30'} bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/40 shadow-[0_0_80px_-15px_rgba(59,130,246,0.55)] overflow-hidden grid md:grid-cols-5`}
              >
                <div className="md:col-span-2 relative bg-slate-800 order-1 md:order-none overflow-hidden">
                  <img
                    src="/event-angie-nixon.jpg"
                    alt="Representative Angie Nixon"
                    className="w-full h-full object-cover aspect-[4/5] md:aspect-auto"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-slate-900/70 pointer-events-none hidden md:block" />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none md:hidden" />
                  {isEventPast ? (
                    <span className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/90 border border-blue-400/50 text-blue-100 text-[10px] font-bold uppercase tracking-widest shadow-lg backdrop-blur">
                      Thank you for joining us
                    </span>
                  ) : (
                    <span className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                      </span>
                      Happening Today
                    </span>
                  )}
                </div>
                <div className="md:col-span-3 relative p-8 md:p-10 flex flex-col h-full">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
                  <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-2">{pub.eyebrow}</p>
                  <h3 className="text-3xl md:text-4xl font-black text-white leading-tight mb-3">
                    {pub.shortTitle || pub.title}
                  </h3>
                  <p className="text-base text-gray-300 italic mb-6 leading-relaxed">{pub.tagline}</p>

                  <div className="border-l-2 border-blue-500 pl-4 mb-6">
                    <p className="text-xs uppercase tracking-widest text-blue-400 font-semibold mb-1">Featuring</p>
                    <p className="text-white font-bold">{pub.featuredGuest}</p>
                    {pub.guestTitle.map((t, i) => <p key={i} className="text-xs text-gray-400">{t}</p>)}
                  </div>

                  <div className="space-y-2 text-sm text-gray-300 mb-6">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-400 shrink-0" />
                      <span>{pub.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-400 shrink-0" />
                      <span>{pub.time}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-white">{pub.venue}</p>
                        <p className="text-xs text-gray-400 whitespace-pre-line">{pub.address}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-y border-blue-500/20 py-3 mb-6 flex items-center justify-center gap-6 text-xs uppercase tracking-widest">
                    <span className="text-blue-400 font-bold">
                      {isEventPast ? 'Recap coming soon' : 'Open to the Public'}
                    </span>
                  </div>

                  <DialogTrigger asChild>
                    <Button
                      data-testid="event-card-public-view-btn"
                      className="w-full mt-auto bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wider py-6 text-sm shadow-lg shadow-blue-500/30 transition-all hover:scale-[1.02]"
                    >
                      {isEventPast ? 'View Event Recap' : 'View Event Details'}
                    </Button>
                  </DialogTrigger>
                </div>
              </div>

              <DialogContent className="max-w-4xl max-h-[90vh] bg-slate-800 border-slate-700 text-white p-0 overflow-hidden">
                <DialogTitle className="sr-only">{pub.title}</DialogTitle>
                <ScrollArea className="max-h-[90vh]">
                  <div data-testid="event-public-modal">
                    <div className="relative bg-slate-900">
                      <img src={pub.image} alt={pub.featuredGuest} className="w-full h-auto" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-800 to-transparent pointer-events-none" />
                    </div>
                    <div className="p-6 md:p-10">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="text-blue-400 text-sm font-bold uppercase tracking-widest">{pub.eyebrow}</span>
                        <span className="inline-block px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest">
                          {pub.accessBadge}
                        </span>
                      </div>
                      <h3 className="text-3xl md:text-4xl font-black text-white leading-tight mb-3">{pub.title}</h3>
                      <p className="text-lg text-gray-300 mb-6">{pub.tagline}</p>

                      <div className="border-l-2 border-blue-500 pl-4 mb-6">
                        <p className="text-xs uppercase tracking-widest text-blue-400 font-semibold mb-1">Featured Guest</p>
                        <p className="text-white font-bold text-lg">{pub.featuredGuest}</p>
                        {pub.guestTitle.map((t, i) => <p key={i} className="text-sm text-gray-400">{t}</p>)}
                      </div>

                      {pub.additionalGuests && pub.additionalGuests.length > 0 && (
                        <div className="border-l-2 border-blue-500/50 pl-4 mb-6">
                          <p className="text-xs uppercase tracking-widest text-blue-400 font-semibold mb-2">Host Committee</p>
                          <p className="text-sm text-gray-300 mb-3">{pub.additionalGuestsIntro}</p>
                          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-white">
                            {pub.additionalGuests.map((g, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-blue-400 shrink-0">•</span>
                                <span>{g}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="grid sm:grid-cols-2 gap-3 mb-6 text-sm">
                        <div className="flex items-start gap-2 text-gray-300">
                          <Calendar className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Date</p>
                            <p>{pub.date}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 text-gray-300">
                          <Clock className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Time</p>
                            <p>{pub.time}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 text-gray-300 sm:col-span-2">
                          <MapPin className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Venue</p>
                            <p className="font-semibold text-white">{pub.venue}</p>
                            <p className="text-sm text-gray-400 whitespace-pre-line">{pub.address}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 text-gray-300 leading-relaxed mb-6">
                        {pub.description.map((p, i) => <p key={i}>{p}</p>)}
                      </div>

                      <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mb-8">
                        {pub.rsvpUrl && (
                          <Button
                            data-testid={`event-modal-rsvp-btn-${pub.id}`}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wider px-8 py-6 text-sm"
                            onClick={() => window.open(pub.rsvpUrl, '_blank', 'noopener,noreferrer')}
                          >
                            RSVP for the Event
                          </Button>
                        )}
                        <Button
                          data-testid={`event-modal-cta-${pub.id}`}
                          className="w-full sm:w-auto bg-pink-500 hover:bg-pink-600 text-white font-bold uppercase tracking-wider px-8 py-6 text-sm shadow-lg shadow-pink-500/30 transition-all hover:scale-105"
                          onClick={() => window.open(pub.ctaUrl, '_blank', 'noopener,noreferrer')}
                        >
                          {pub.ctaLabel}
                        </Button>
                        {pub.sponsorshipUrl && (
                          <Button
                            data-testid={`event-modal-sponsorship-btn-${pub.id}`}
                            variant="outline"
                            className="w-full sm:w-auto border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-bold uppercase tracking-wider px-8 py-6 text-sm"
                            onClick={() => window.open(pub.sponsorshipUrl, '_blank', 'noopener,noreferrer')}
                          >
                            Sponsorship Opportunities
                          </Button>
                        )}
                      </div>

                      {renderEndorsementShare(pub.slug, pub.title)}
                    </div>
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
            ); })()}

            {isEventPast && (
              <div className="mt-10 space-y-8">
                {/* Reception Recap Carousel (community + leadership) */}
                <RecapCarousel />

                {/* Sponsors Thank You */}
                <div
                  id="sponsors"
                  data-testid="sponsors-section"
                  className="rounded-2xl border border-blue-500/20 bg-slate-900/60 p-8 md:p-10 scroll-mt-24"
                >
                  <div className="text-center mb-8">
                    <p className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2">With Gratitude</p>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-2">Thank You to Our Sponsors</h3>
                    <p className="text-gray-400 text-sm max-w-2xl mx-auto">
                      This afternoon was possible because of leaders who believe in a new way to Democrat. We are grateful to the partners below and to the campaign of Angie Nixon.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      { name: 'A. Scott Bolden', logo: '/sponsor-a-scott-bolden.webp', bg: 'bg-[#0b1a3f]', href: 'https://www.ascottbolden.com' },
                      { name: 'Reggie Van Lee', logo: '/sponsor-reggie-van-lee.jpg', bg: 'bg-black', href: 'https://www.linkedin.com/in/reggievanlee' },
                      { name: 'M.E. Melton Enterprises', logo: '/sponsor-mem-enterprises.jpg', bg: 'bg-black', href: 'https://www.linkedin.com/in/michael-e-melton-860aa419/' },
                      { name: 'Preventive Measures', logo: '/sponsor-preventive-measures.png', bg: 'bg-white', href: 'https://www.preventivemeasuresinc.com' },
                      { name: 'Urban Garden Brewing', logo: '/sponsor-urban-garden.jpeg', bg: 'bg-white', href: 'https://www.urbangardenbrewing.com', pad: 'p-3 md:p-4' },
                      { name: 'Angie Nixon for Florida', logo: '/sponsor-angie-nixon-florida.png', bg: 'bg-slate-800', href: 'https://angienixon.com' },
                    ].map((s, i) => (
                      <a
                        key={i}
                        data-testid={`sponsor-thanks-${i}`}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit ${s.name}`}
                        title={s.name}
                        className={`rounded-lg overflow-hidden flex items-center justify-center aspect-video ${s.pad || 'p-1.5 md:p-2'} ${s.bg} ring-1 ring-white/5 transition-all hover:ring-blue-400/60 hover:scale-[1.03] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400`}
                      >
                        <img
                          src={s.logo}
                          alt={s.name}
                          className="max-h-full max-w-full object-contain"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Host Committee Thank You */}
                <div
                  id="host-committee"
                  data-testid="host-committee-section"
                  className="rounded-2xl border border-blue-500/20 bg-slate-900/60 p-8 md:p-10 scroll-mt-24"
                >
                  <div className="text-center mb-8">
                    <p className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2">Host Committee</p>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-2">Thank You to Our Host Committee</h3>
                    <p className="text-gray-400 text-sm max-w-2xl mx-auto">
                      Our host committee opened their doors, their networks, and their time to make this reception a reality. We are grateful to each of them.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { name: 'U.S. Senator Angela Alsobrooks', where: 'MD', href: 'https://www.alsobrooks.senate.gov/' },
                      { name: 'Cora Masters Barry', where: 'Forever D.C. First Lady', href: 'https://dcarts.dc.gov/commission/cora-masters-barry-ward-8' },
                      { name: 'Hon. Sputty Cephas', where: 'MD', href: 'https://www.choosecambridge.com/311/Fourth-Ward-Commissioner-Sputty-Cephas' },
                      { name: 'Hon. Brian Cunningham', where: 'NY', href: 'https://assembly.ny.gov/mem/Brian-Cunningham' },
                      { name: 'Hon. Hamilton Grant', where: 'SC', href: 'https://www.grantforsc.com/' },
                      { name: 'Hon. Jeremy Gray', where: 'AL', href: 'https://jeremygrayforalhouse83.com/' },
                      { name: 'Hon. Chioma Iwuoha', where: 'DC', href: 'https://www.dcdemocraticparty.org/exective-candidates' },
                      { name: 'Hon. Shireka McCarthy', where: 'MD', href: 'https://seatpleasantmd.gov/161/Hon-Shireka-McCarthy-At-Large' },
                      { name: 'Hon. Oye Owolewa', where: 'DC', href: 'https://www.vote4oye.com/' },
                      { name: 'Hon. Zachary Parker', where: 'DC', href: 'https://dccouncil.gov/council/ward-5-councilmember-zachary-parker/' },
                      { name: 'Hon. Jacque Patterson', where: 'DC', href: 'https://sboe.dc.gov/biography/jacque-patterson-large' },
                      { name: 'Hon. Denise Roberts', where: 'MD', href: 'https://mgaleg.maryland.gov/mgawebsite/Members/Details/roberts01' },
                      { name: 'Hon. Keith Sydnor', where: 'MD', href: 'https://www.cityoflaurel.org/295/Office-of-the-Mayor' },
                      { name: 'Hon. Deon Tedder', where: 'SC', href: 'https://deontedder.com/' },
                      { name: 'Hon. LaTasha Ward', where: 'MD', href: 'https://latashaward.com/' },
                      { name: 'Hon. Rahsheim Wright', where: 'MD', href: 'https://rahsheimwright.org/' },
                      { name: 'Hon. RaShon Young', where: 'FL', href: 'https://www.rashonyoung.com/' },
                    ].map((m, i) => (
                      <a
                        key={i}
                        data-testid={`host-committee-${i}`}
                        href={m.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between gap-3 rounded-lg border border-blue-500/20 bg-slate-800/60 hover:bg-slate-800 hover:border-blue-400/60 px-4 py-3 transition-all"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-semibold leading-tight truncate group-hover:text-blue-200 transition-colors">
                            {m.name}
                          </p>
                          <p className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mt-0.5">{m.where}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-blue-400 shrink-0 transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Contribute to Angie CTA */}
                <div className="relative rounded-2xl border border-pink-500/40 bg-gradient-to-br from-blue-950 via-slate-900 to-pink-950/50 p-8 md:p-10 overflow-hidden">
                  <div className="absolute -top-20 -right-20 w-60 h-60 bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
                  <div className="relative grid md:grid-cols-3 gap-6 items-center">
                    <div className="md:col-span-2">
                      <p className="text-pink-400 text-xs font-bold uppercase tracking-[0.25em] mb-2">Keep the Momentum</p>
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight">
                        The reception ended. The <span className="text-pink-400">fight for Florida</span> is just beginning.
                      </h3>
                      <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                        Angie Nixon is running for the United States Senate to bring a new way to Democrat home to Florida. If today moved you, chip in and help send her to Washington.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 md:items-end">
                      <a
                        data-testid="angie-donate-cta"
                        href="https://secure.actblue.com/donate/common.sense.call.me.senator"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full md:w-auto bg-pink-500 hover:bg-pink-600 text-white font-black uppercase tracking-wider px-8 py-5 text-sm rounded-full shadow-lg shadow-pink-500/30 transition-all hover:scale-[1.03]"
                      >
                        Contribute to Angie
                      </a>
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 text-center md:text-right">Powered by ActBlue</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* Latest Endorsements Section */}
      <section className="py-12 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-white mb-6 text-center">Latest Endorsements</h2>
          <p className="text-xl text-gray-300 mb-12 text-center max-w-3xl mx-auto">
            Supporting pragmatic leaders who deliver results for their communities
          </p>

          {/* Featured Endorsement - Angie Nixon (Horizontal) */}
          <Card
            data-testid="endorsement-nixon-card"
            className="mb-10 bg-slate-800 border-blue-500/60 hover:border-blue-400 transition-all duration-300 overflow-hidden shadow-[0_0_80px_-15px_rgba(59,130,246,0.55)]"
          >
            <div className="grid md:grid-cols-5">
              <div className="md:col-span-2 relative overflow-hidden bg-slate-900 aspect-[4/5] md:aspect-auto">
                <img
                  src="/event-angie-nixon.jpg"
                  alt="Rep. Angie Nixon for U.S. Senate, CommonSense Democrats Endorsement"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-slate-800/70 pointer-events-none hidden md:block" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg">
                  Newest Endorsement
                </span>
              </div>
              <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                <span className="inline-block self-start px-3 py-1 mb-3 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest">
                  U.S. Senate Florida
                </span>
                <h3 className="text-white text-3xl md:text-4xl font-black leading-tight mb-3">
                  Angie Nixon for U.S. Senate
                </h3>
                <p className="text-blue-400 text-lg font-medium mb-4">
                  Florida is Where a New Way to Democrat Begins.
                </p>
                <p className="text-gray-300 leading-relaxed mb-6">
                  A working mother, a Florida State Representative, and now Democratic Nominee for the United States Senate. Angie Nixon is the kind of leader we need in Washington: unafraid, unbought, and unmistakably of the people she represents.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Dialog open={openModal === 'endorsement-nixon'} onOpenChange={(o) => handleModalChange('endorsement-nixon', o)}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="endorsement-nixon-read-full"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 text-sm"
                      >
                        Read Full Endorsement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                      <DialogHeader>
                        <DialogTitle className="text-3xl text-white mb-2">
                          CommonSense Democrats Endorses Angie Nixon for U.S. Senate
                        </DialogTitle>
                        <p className="text-lg text-blue-400">
                          Florida is Where a New Way to Democrat Begins.
                        </p>
                      </DialogHeader>
                      <ScrollArea className="max-h-[65vh] pr-4">
                        <img
                          src="/event-angie-nixon.jpg"
                          alt="Angie Nixon for U.S. Senate"
                          className="w-full rounded-lg mb-6 border border-slate-700"
                          loading="lazy"
                        />
                        <div className="space-y-5 text-gray-300 leading-relaxed">
                          <p>
                            For too long, national Democrats have treated Florida as a lost cause and told working families they should be grateful for whatever leadership was on offer. Angie Nixon has never accepted that math, and neither should we.
                          </p>
                          <p className="text-white font-semibold">
                            CommonSense Democrats is proud to endorse Representative Angie Nixon for the United States Senate.
                          </p>
                          <p>
                            Angie is a working mother, a small business owner, a lifelong organizer, and a three-term Florida State Representative who has spent her entire career fighting for the people others were willing to write off. She has stood on picket lines with warehouse workers, slept overnight on the House floor to protest a six-week abortion ban, and taken on her own party leadership when they were more interested in comfort than courage.
                          </p>
                          <p>
                            That is not a resume built for a Senate portrait. It is a resume built for the fight ahead.
                          </p>
                          <p>
                            Florida is not a red state. It is a state where working people have been outspent, outmaneuvered, and told for a decade that showing up is somehow the same as winning. The Democratic Party lost Florida the same way it has lost too much of the country: by talking past the people it needs and pretending the loss was inevitable.
                          </p>
                          <p>Angie Nixon offers something different.</p>
                          <p>
                            She speaks plainly about the cost of housing, groceries, insurance, child care, and prescriptions because those are the conversations her neighbors are actually having. She is willing to hold Democrats accountable when we fall short and willing to build coalitions with anyone, of any party, who is serious about delivering for working families.
                          </p>
                          <p>
                            That is the kind of senator Florida deserves. That is the kind of senator the country needs.
                          </p>
                          <p className="text-white font-semibold">
                            CommonSense Democrats believes the future of our party belongs to leaders who can speak to economic insecurity without condescension, defend democratic institutions without making politics their entire identity, challenge concentrated power without apology, and still remember that neighbors who disagree with us are neighbors, not enemies.
                          </p>
                          <p>Angie Nixon meets that test.</p>
                          <p>
                            The fight for Florida is not a footnote. It is the frontline. And on that frontline, we are proud to stand with Angie.
                          </p>
                          <p className="text-white font-semibold">
                            CommonSense Democrats proudly endorses Representative Angie Nixon for the United States Senate from Florida.
                          </p>
                        </div>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                          <Button
                            data-testid="endorsement-nixon-modal-donate"
                            className="bg-pink-500 hover:bg-pink-600 text-white font-semibold"
                            onClick={() => window.open('https://secure.actblue.com/donate/common.sense.call.me.senator', '_blank', 'noopener,noreferrer')}
                          >
                            Contribute to Angie
                          </Button>
                        </div>
                        {renderEndorsementShare('endorsement-nixon', 'Angie Nixon for U.S. Senate')}
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                  <Button
                    data-testid="endorsement-nixon-donate"
                    className="bg-pink-500 hover:bg-pink-600 text-white font-semibold py-3 px-6 text-sm"
                    onClick={() => window.open('https://secure.actblue.com/donate/common.sense.call.me.senator', '_blank', 'noopener,noreferrer')}
                  >
                    Contribute to Angie
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card
              data-testid="endorsement-talarico-card"
              className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 overflow-hidden group flex flex-col"
            >
              <div className="relative overflow-hidden bg-slate-900 aspect-[4/5]">
                <img
                  src="/endorsement-james-talarico.jpg"
                  alt="James Talarico for U.S. Senate - CommonSense Democrats Endorsement"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col flex-1">
                <CardHeader>
                  <span className="inline-block self-center px-3 py-1 mb-2 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest text-center">
                    U.S. Senate Texas
                  </span>
                  <CardTitle className="text-white text-2xl md:text-3xl leading-tight">
                    James Talarico for U.S. Senate
                  </CardTitle>
                  <CardDescription className="text-blue-400 text-base font-medium">
                    Texas Has Changed. Democrats Should Have the Courage to Change With It.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="mt-auto flex flex-col gap-2">
                    <Dialog open={openModal === 'endorsement-talarico'} onOpenChange={(o) => handleModalChange('endorsement-talarico', o)}>
                      <DialogTrigger asChild>
                        <Button
                          data-testid="endorsement-talarico-read-full"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                        >
                          Read Full Endorsement
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-3xl text-white mb-2">
                            CommonSense Democrats Endorses James Talarico for U.S. Senate
                          </DialogTitle>
                          <p className="text-lg text-blue-400">
                            Texas Has Changed. Democrats Should Have the Courage to Change With It.
                          </p>
                        </DialogHeader>
                        <ScrollArea className="max-h-[65vh] pr-4">
                          <img
                            src="/endorsement-james-talarico.jpg"
                            alt="James Talarico for U.S. Senate"
                            className="w-full rounded-lg mb-6 border border-slate-700"
                            loading="lazy"
                          />
                          <div className="space-y-5 text-gray-300 leading-relaxed">
                            <p>
                              For years, Democrats have talked about Texas as though it were a political destination perpetually visible on the horizon: growing, diversifying, changing, but somehow never quite ready to become genuinely competitive.
                            </p>
                            <p>
                              James Talarico is making a compelling case that the problem may not have been Texas. It may have been the kind of politics Democrats were offering Texans.
                            </p>
                            <p className="text-white font-semibold">
                              CommonSense Democrats is proud to endorse James Talarico for the United States Senate.
                            </p>
                            <p>
                              Talarico represents something the Democratic Party badly needs: generational change accompanied by an understanding that winning elections requires more than assembling the right collection of policy positions. It requires connecting those policies to the lives people are actually living.
                            </p>
                            <p>
                              An eighth-generation Texan, former public school teacher and current state representative, Talarico has built his political identity around working families, public education, economic opportunity and a willingness to challenge concentrated political and economic power. He first won office in 2018 by flipping a Texas House district Donald Trump had carried two years earlier. In the Legislature, he has worked on public schools, job opportunities, child care, housing and prescription drug costs.
                            </p>
                            <p>There is a lesson in that record.</p>
                            <p>
                              Americans do not experience the economy through government statistics. They experience it at the grocery store, when the rent is due, when the property tax bill arrives, when they pay for child care or when they discover what a prescription will cost.
                            </p>
                            <p>Democrats should be the party that understands that instinctively.</p>
                            <p>
                              Talarico also understands something else that has been missing from too much Democratic politics: persuasion matters. There are Republicans worth talking to, independents worth listening to and millions of people who do not organize their lives around political parties at all. A political movement interested only in speaking to people who already agree with it is not really a movement. It is a club.
                            </p>
                            <p>Texas offers Democrats an opportunity to prove they can build something larger.</p>
                            <p>
                              Talarico is now running against Republican Ken Paxton in what has developed into one of the country's most closely watched Senate contests. His campaign has demonstrated unusual fundraising strength, with the Texas Tribune reporting that he raised more at this stage than any Texas general-election candidate in the previous decade.
                            </p>
                            <p>But our endorsement is not about whether political handicappers believe Texas is finally ready to turn blue.</p>
                            <p className="text-white font-semibold">It is about whether Texans are ready for something better.</p>
                            <p>
                              CommonSense Democrats believes the party's future belongs to leaders who can speak plainly about economic insecurity, defend democratic institutions without making politics their entire identity, challenge powerful interests and still recognize that people who disagree with us are fellow Americans rather than enemies.
                            </p>
                            <p>We will not agree with every candidate we endorse on every issue. That is not our standard.</p>
                            <p>
                              We are looking for judgment. Character. Independence. Competence. An understanding of working people. And the ability to build coalitions large enough to actually govern.
                            </p>
                            <p className="text-white font-semibold">James Talarico meets that test.</p>
                            <p>
                              Texas does not need another senator who sees public office primarily as a battlefield for America's endless political war. It needs someone interested in what happens after the campaign ends and governing begins.
                            </p>
                            <p>The Democratic Party could use more leaders who understand the difference.</p>
                            <p className="text-white font-semibold">
                              CommonSense Democrats proudly endorses James Talarico for the United States Senate from Texas.
                            </p>
                          </div>
                          <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button
                              data-testid="endorsement-talarico-modal-visit"
                              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              onClick={() => window.open('https://jamestalarico.com', '_blank', 'noopener,noreferrer')}
                            >
                              Visit Website
                            </Button>
                          </div>
                          {renderEndorsementShare('endorsement-talarico', 'James Talarico for U.S. Senate')}
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                    <Button
                      data-testid="endorsement-talarico-visit"
                      variant="outline"
                      className="w-full border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                      onClick={() => window.open('https://jamestalarico.com', '_blank', 'noopener,noreferrer')}
                    >
                      Visit Website
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>

            <Card
              data-testid="endorsement-plaskett-card"
              className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 overflow-hidden group flex flex-col"
            >
              <div className="relative overflow-hidden bg-slate-900 aspect-[4/5]">
                <img
                  src="/endorsement-stacey-plaskett.jpg"
                  alt="Stacey Plaskett for Governor - CommonSense Democrats Endorsement"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col flex-1">
                <CardHeader>
                  <span className="inline-block self-center px-3 py-1 mb-2 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest text-center">
                    Governor U.S. Virgin Islands
                  </span>
                  <CardTitle className="text-white text-2xl md:text-3xl leading-tight">
                    Stacey Plaskett for Governor
                  </CardTitle>
                  <CardDescription className="text-blue-400 text-base font-medium">
                    Experience Should Be Measured by What It Delivers.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="mt-auto flex flex-col gap-2">
                    <Dialog open={openModal === 'endorsement-plaskett'} onOpenChange={(o) => handleModalChange('endorsement-plaskett', o)}>
                      <DialogTrigger asChild>
                        <Button
                          data-testid="endorsement-plaskett-read-full"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                        >
                          Read Full Endorsement
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-3xl text-white mb-2">
                            CommonSense Democrats Endorses Stacey Plaskett for Governor
                          </DialogTitle>
                          <p className="text-lg text-blue-400">
                            Experience Should Be Measured by What It Delivers.
                          </p>
                        </DialogHeader>
                        <ScrollArea className="max-h-[65vh] pr-4">
                          <img
                            src="/endorsement-stacey-plaskett.jpg"
                            alt="Stacey Plaskett for Governor"
                            className="w-full rounded-lg mb-6 border border-slate-700"
                            loading="lazy"
                          />
                          <div className="space-y-5 text-gray-300 leading-relaxed">
                            <p>
                              There is a tendency in American politics to treat change and experience as opposing ideas. They are not. Sometimes progress requires a new generation willing to challenge the way things have always been done. Other times, it requires someone who understands government well enough to make it work. In the U.S. Virgin Islands, CommonSense Democrats believes Stacey Plaskett has the experience, relationships and understanding of government necessary to turn opportunity into results.
                            </p>
                            <p>
                              Plaskett has represented the Virgin Islands in Congress since 2015, navigating a federal system in which Virgin Islanders are American citizens but have no senators and no voting representative on final legislation in the House. She has spent more than a decade making sure the territory has a voice in Washington, helping secure billions in federal support while building relationships across the federal government.
                            </p>
                            <p>
                              But securing money is not the same as improving people's lives. That distinction is central to why we support her campaign for governor. The Virgin Islands have significant federal resources available for recovery and infrastructure, yet residents still confront unreliable electricity, housing pressures, health care challenges, aging public facilities and projects that take too long to move from announcement to completion. The next governor must be judged not by how much money government has available, but by what actually gets done with it.
                            </p>
                            <p>
                              Plaskett's experience gives her an opportunity to close that gap. She understands Washington, but she also understands the communities those federal programs are supposed to serve. If elected, her administration should be measured by straightforward questions: Are the lights more reliable? Are schools improving? Is housing becoming more attainable? Are infrastructure projects actually being completed? Is government working better for the people who depend on it?
                            </p>
                            <p>
                              That is the kind of accountability CommonSense Democrats believes should define public service. We are not interested in ideological uniformity or political labels. We are interested in leaders who understand the people they represent, exercise sound judgment and recognize that government ultimately has to produce results.
                            </p>
                            <p>
                              After more than a decade advocating for the Virgin Islands in Washington, Plaskett is asking voters for the opportunity to bring that experience home. We believe she has earned it.
                            </p>
                            <p className="text-white font-semibold">
                              CommonSense Democrats proudly endorses Stacey Plaskett for Governor of the U.S. Virgin Islands and Milton Potter for Lieutenant Governor.
                            </p>
                          </div>
                          <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button
                              data-testid="endorsement-plaskett-modal-visit"
                              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              onClick={() => window.open('https://plaskettpotter.com', '_blank', 'noopener,noreferrer')}
                            >
                              Visit Website
                            </Button>
                          </div>
                          {renderEndorsementShare('endorsement-plaskett', 'Stacey Plaskett for Governor of the U.S. Virgin Islands')}
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                    <Button
                      data-testid="endorsement-plaskett-visit"
                      variant="outline"
                      className="w-full border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                      onClick={() => window.open('https://plaskettpotter.com', '_blank', 'noopener,noreferrer')}
                    >
                      Visit Website
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>

            <Card
              data-testid="endorsement-crowley-card"
              className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 overflow-hidden group flex flex-col"
            >
              <div className="relative overflow-hidden bg-slate-900 aspect-[4/5]">
                <img
                  src="/endorsement-david-crowley.jpg"
                  alt="David Crowley for Governor of Wisconsin - CommonSense Democrats Endorsement"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col flex-1">
                <CardHeader>
                  <span className="inline-block self-center px-3 py-1 mb-2 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest text-center">
                    Governor Wisconsin
                  </span>
                  <CardTitle className="text-white text-2xl md:text-3xl leading-tight">
                    David Crowley for Governor
                  </CardTitle>
                  <CardDescription className="text-blue-400 text-base font-medium">
                    Wisconsin Needs a Governor Who Knows How to Govern.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="mt-auto flex flex-col gap-2">
                    <Dialog open={openModal === 'endorsement-crowley'} onOpenChange={(o) => handleModalChange('endorsement-crowley', o)}>
                      <DialogTrigger asChild>
                        <Button
                          data-testid="endorsement-crowley-read-full"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                        >
                          Read Full Endorsement
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-3xl text-white mb-2">
                            CommonSense Democrats Endorses David Crowley for Governor of Wisconsin
                          </DialogTitle>
                          <p className="text-lg text-blue-400">
                            Wisconsin Needs a Governor Who Knows How to Govern.
                          </p>
                        </DialogHeader>
                        <ScrollArea className="max-h-[65vh] pr-4">
                          <img
                            src="/endorsement-david-crowley.jpg"
                            alt="David Crowley for Governor of Wisconsin"
                            className="w-full rounded-lg mb-6 border border-slate-700"
                            loading="lazy"
                          />
                          <div className="space-y-5 text-gray-300 leading-relaxed">
                            <p>
                              Politics rewards promises. Governing requires something harder: deciding what can actually be accomplished, bringing people together to accomplish it and accepting responsibility for the result.
                            </p>
                            <p className="text-white font-semibold">
                              That distinction is one reason CommonSense Democrats proudly endorses David Crowley for Governor of Wisconsin.
                            </p>
                            <p>
                              Crowley enters the general election with experience that matters. As Milwaukee County executive, he has governed Wisconsin's largest and most diverse county, working in a state where partisan divisions can make even practical problems unnecessarily difficult to solve. His record includes helping negotiate a bipartisan agreement with Republican legislators that increased resources for local governments, supporting the creation of affordable housing and overseeing balanced county budgets.
                            </p>
                            <p>
                              His biography also gives him an understanding of economic insecurity that cannot be manufactured by consultants. Crowley has spoken about growing up with parents struggling with addiction and mental illness, experiencing evictions and sometimes not knowing whether his family would have food, heat or electricity. Those experiences help explain why his campaign is focused so heavily on the basic costs confronting Wisconsin families.
                            </p>
                            <p className="text-white font-semibold">Housing. Child care. Health care. Energy. Jobs and wages.</p>
                            <p>
                              These issues may not generate the most attention on cable television, but they determine whether families believe the economy and their government are working for them. Crowley's economic agenda focuses on expanding housing, strengthening apprenticeship programs, supporting small businesses, improving access to health care and child care, and creating good-paying jobs through infrastructure and energy investment.
                            </p>
                            <p>
                              What particularly appeals to CommonSense Democrats is Crowley's understanding that pragmatism does not require abandoning Democratic values. It requires figuring out how to turn those values into results. Wisconsin is politically competitive and deeply divided. Its next governor will need to represent people who voted for him, people who voted against him and plenty of residents who are simply exhausted by politics altogether.
                            </p>
                            <p>Crowley has already demonstrated that he can operate in that environment. Now he has an opportunity to take that approach statewide.</p>
                            <p>
                              CommonSense Democrats believes the Democratic Party needs leaders who can defend our principles without losing sight of why people elect governments in the first place. Families want safe communities, affordable homes, strong schools, good jobs and a reasonable chance to build a better life. They also deserve leaders capable of working with people they disagree with when doing so produces something worthwhile.
                            </p>
                            <p>That is not ideological compromise for its own sake.</p>
                            <p className="text-white font-semibold">It is governing.</p>
                            <p className="text-white font-semibold">
                              CommonSense Democrats proudly endorses David Crowley for Governor of Wisconsin.
                            </p>
                          </div>
                          <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button
                              data-testid="endorsement-crowley-modal-visit"
                              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              onClick={() => window.open('https://davidcrowleyforwi.com', '_blank', 'noopener,noreferrer')}
                            >
                              Visit Website
                            </Button>
                          </div>
                          {renderEndorsementShare('endorsement-crowley', 'David Crowley for Governor of Wisconsin')}
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                    <Button
                      data-testid="endorsement-crowley-visit"
                      variant="outline"
                      className="w-full border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                      onClick={() => window.open('https://davidcrowleyforwi.com', '_blank', 'noopener,noreferrer')}
                    >
                      Visit Website
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>

            <Card
              data-testid="endorsement-bottoms-card"
              className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 overflow-hidden group flex flex-col"
            >
              <div className="relative overflow-hidden bg-slate-900 aspect-[4/5]">
                <img
                  src="/endorsement-keisha-lance-bottoms.jpg"
                  alt="Keisha Lance Bottoms for Governor of Georgia - CommonSense Democrats Endorsement"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col flex-1">
                <CardHeader>
                  <span className="inline-block self-center px-3 py-1 mb-2 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest text-center">
                    Governor Georgia
                  </span>
                  <CardTitle className="text-white text-2xl md:text-3xl leading-tight">
                    Keisha Lance Bottoms for Governor
                  </CardTitle>
                  <CardDescription className="text-blue-400 text-base font-medium">
                    Georgia Has Changed. Its Leadership Should Reflect It.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="mt-auto flex flex-col gap-2">
                    <Dialog open={openModal === 'endorsement-bottoms'} onOpenChange={(o) => handleModalChange('endorsement-bottoms', o)}>
                      <DialogTrigger asChild>
                        <Button
                          data-testid="endorsement-bottoms-read-full"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                        >
                          Read Full Endorsement
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-3xl text-white mb-2">
                            CommonSense Democrats Endorses Keisha Lance Bottoms for Governor of Georgia
                          </DialogTitle>
                          <p className="text-lg text-blue-400">
                            Georgia Has Changed. Its Leadership Should Reflect It.
                          </p>
                        </DialogHeader>
                        <ScrollArea className="max-h-[65vh] pr-4">
                          <img
                            src="/endorsement-keisha-lance-bottoms.jpg"
                            alt="Keisha Lance Bottoms for Governor of Georgia"
                            className="w-full rounded-lg mb-6 border border-slate-700"
                            loading="lazy"
                          />
                          <div className="space-y-5 text-gray-300 leading-relaxed">
                            <p>
                              Georgia has spent much of the past decade demonstrating that the old political assumptions about the South no longer hold. Its communities are growing, its economy is changing and its electorate has become one of the most competitive in the country. Yet many Georgia families are asking a question that transcends all of those political changes: Why has it become so difficult to afford an ordinary life?
                            </p>
                            <p className="text-white font-semibold">
                              That is one reason CommonSense Democrats proudly endorses Keisha Lance Bottoms for Governor of Georgia.
                            </p>
                            <p>
                              Bottoms brings something important to the race: executive experience combined with an understanding of the communities she wants to govern. As mayor of Atlanta, she led one of America's largest cities through an extraordinarily difficult period that included a global pandemic, economic disruption and intense national debates over policing and public safety. She later served in the White House, giving her experience at the local and federal levels before returning home to seek Georgia's highest office.
                            </p>
                            <p>
                              But resumes alone do not lower anyone's rent or utility bill. Bottoms' campaign has focused on the economic pressures Georgians encounter every day, including housing costs, health-care premiums, utilities and the difficulties facing small businesses. Those concerns are not particularly ideological. They are the practical measurements families use to decide whether government and the economy are working for them.
                            </p>
                            <p>
                              Georgia also occupies a special place in the country's continuing debate over democracy and voting rights. Bottoms has proposed stronger state protections for voting access, election workers and fair representation. Those issues matter in every state, but they carry particular weight in Georgia, where elections have repeatedly become national tests of whether political competition strengthens democracy or becomes an excuse to restrict it.
                            </p>
                            <p>
                              CommonSense Democrats believes Democrats are strongest when they connect those democratic principles to competent everyday government. Protecting the right to vote matters. So does making housing affordable, strengthening schools, supporting businesses, keeping communities safe and creating an economy in which working families can get ahead.
                            </p>
                            <p>
                              Bottoms has already been tested in executive office. She understands that governing a diverse state requires representing people who supported you and people who did not. Georgia does not need another four years of politics organized around permanent partisan conflict. It needs leadership focused on what government can actually accomplish for its people.
                            </p>
                            <p>
                              Keisha Lance Bottoms has the experience to understand that distinction and the opportunity to demonstrate it statewide.
                            </p>
                            <p className="text-white font-semibold">
                              CommonSense Democrats proudly endorses Keisha Lance Bottoms for Governor of Georgia.
                            </p>
                          </div>
                          <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button
                              data-testid="endorsement-bottoms-modal-visit"
                              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              onClick={() => window.open('https://www.keishaforgovernor.com', '_blank', 'noopener,noreferrer')}
                            >
                              Visit Website
                            </Button>
                          </div>
                          {renderEndorsementShare('endorsement-bottoms', 'Keisha Lance Bottoms for Governor of Georgia')}
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                    <Button
                      data-testid="endorsement-bottoms-visit"
                      variant="outline"
                      className="w-full border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-semibold whitespace-normal h-auto py-2 text-xs"
                      onClick={() => window.open('https://www.keishaforgovernor.com', '_blank', 'noopener,noreferrer')}
                    >
                      Visit Website
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Executive Director Announcement Section - archived, gated by SHOW_ED_CONTENT */}
      {SHOW_ED_CONTENT && (
      <section className="py-12 px-6 bg-gradient-to-b from-slate-900 to-blue-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Headshot */}
            <div className="order-2 md:order-1">
              <div className="relative">
                <div className="aspect-square rounded-lg overflow-hidden border-4 border-blue-500 shadow-2xl">
                  <img 
                    src="/derrell-simpson-headshot.jpg"
                    alt="Derrell Simpson, Executive Director"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Announcement Content */}
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                CommonSense Democrats Appoints Derrell Simpson as Executive Director
              </h2>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                Veteran public servant, political strategist, and community leader to help lead the organization's next chapter of growth.
              </p>
              <p className="text-gray-300 mb-8 leading-relaxed">
                CommonSense Democrats is pleased to announce the appointment of Derrell Simpson as Executive Director. In his new role, Simpson will oversee the organization's day-to-day operations, lead strategic initiatives, and help expand CommonSense Democrats' work supporting candidates and promoting practical Democratic leadership across the country.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Dialog open={openModal === 'ed-announcement'} onOpenChange={(o) => handleModalChange('ed-announcement', o)}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                      Read Full Announcement
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh] bg-slate-800 border-slate-700 text-white">
                    <DialogHeader>
                      <DialogTitle className="text-3xl text-white mb-4">
                        CommonSense Democrats Appoints Derrell Simpson as Executive Director
                      </DialogTitle>
                      <p className="text-lg text-blue-400 mb-4">
                        Veteran public servant, political strategist, and community leader to help lead the organization's next chapter of growth.
                      </p>
                    </DialogHeader>
                    <ScrollArea className="max-h-[60vh] pr-4">
                      <img
                        src="/articles/ed-announcement.jpg"
                        alt="CommonSense Democrats Appoints Derrell Simpson as Executive Director"
                        data-testid="article-image-announcement"
                        className="w-full rounded-lg mb-6 border border-slate-700"
                        loading="lazy"
                      />
                      <div className="space-y-6 text-gray-300">
                        <p className="leading-relaxed">
                          For twenty four years, Sean Metcalf has known Derrell Simpson as a leader whose work has always centered on one idea: public service is about people.
                        </p>
                        <p className="leading-relaxed">
                          From classrooms and community organizations to government agencies and political campaigns, Simpson has spent more than two decades helping communities solve problems, build partnerships, and strengthen civic engagement. Today, that commitment enters a new chapter.
                        </p>
                        <p className="leading-relaxed font-semibold text-white">
                          CommonSense Democrats is pleased to announce the appointment of Derrell Simpson as Executive Director.
                        </p>
                        <p className="leading-relaxed">
                          In his new role, Simpson will oversee the organization's day to day operations, lead strategic initiatives, strengthen partnerships, and help expand CommonSense Democrats' work supporting candidates, engaging voters, and promoting practical Democratic leadership across the country.
                        </p>
                        
                        {/* Sean Metcalf Quote */}
                        <blockquote className="border-l-4 border-blue-500 pl-6 py-4 my-6 bg-slate-700/50 rounded-r">
                          <p className="text-lg italic text-gray-200 mb-3 leading-relaxed">
                            "Over the last twenty four years, I've watched Derrell lead with integrity, humility, and a genuine commitment to serving others. Whether he was working in education, government, or political organizing, he always understood that leadership begins by listening. Those are the values that define our organization, and I couldn't be more excited to welcome him as Executive Director."
                          </p>
                          <cite className="text-blue-400 font-semibold not-italic">
                            - Sean Metcalf, Founder of CommonSense Democrats
                          </cite>
                        </blockquote>

                        <p className="leading-relaxed">
                          Simpson's career spans education, government, business, and political strategy. He began as an educator before leading community school initiatives that strengthened partnerships between schools and families. He later became the youngest person appointed to the District of Columbia Commission on National and Community Service, where he helped expand volunteer programs and community partnerships throughout the city. He also served in senior leadership roles with the Council of the District of Columbia, advised local governments, founded and helped lead successful consulting firms, and has managed or advised political campaigns across multiple states for more than two decades.
                        </p>

                        {/* Derrell Simpson Quote */}
                        <blockquote className="border-l-4 border-blue-500 pl-6 py-4 my-6 bg-slate-700/50 rounded-r">
                          <p className="text-lg italic text-gray-200 mb-3 leading-relaxed">
                            "I've always believed that meaningful leadership starts with listening. People want leaders who understand their challenges, value collaboration, and focus on delivering results. That's what attracted me to CommonSense Democrats. I look forward to working with our supporters, candidates, and community partners as we continue building an organization that puts service before politics."
                          </p>
                          <cite className="text-blue-400 font-semibold not-italic">
                            - Derrell Simpson, Executive Director
                          </cite>
                        </blockquote>

                        <p className="leading-relaxed">
                          Simpson joins CommonSense Democrats at a time of continued growth for the organization. As Executive Director, he will help expand outreach efforts, strengthen community partnerships, support candidates who are committed to effective leadership, and continue building an organization dedicated to common sense solutions and meaningful civic engagement.
                        </p>

                        <p className="leading-relaxed font-semibold text-white text-lg">
                          Please join us in welcoming Derrell Simpson as Executive Director.
                        </p>

                        <a
                          data-testid="download-press-release"
                          href="/press/commonsense-democrats-derrell-simpson-press-release.pdf"
                          download="CommonSense-Democrats-Appoints-Derrell-Simpson-as-Executive-Director.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-3 mt-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors shadow-lg"
                        >
                          <Download className="w-4 h-4" />
                          Download Press Release (PDF)
                        </a>

                        <div className="bg-slate-700/50 p-6 rounded-lg mt-8">
                          <h4 className="text-white font-semibold text-lg mb-2">About CommonSense Democrats</h4>
                          <p className="text-gray-300 leading-relaxed">
                            CommonSense Democrats is committed to supporting leaders who believe effective government begins with accountability, service, and practical solutions. Through voter engagement, strategic communications, candidate support, and community partnerships, the organization works to strengthen democracy and help communities thrive.
                          </p>
                        </div>
                      </div>
                    </ScrollArea>
                    <div className="border-t border-slate-700 pt-4 mt-2 flex flex-wrap items-center gap-3">
                      <span className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Share:</span>
                      <a
                        data-testid="share-twitter-announcement"
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('CommonSense Democrats Appoints Derrell Simpson as Executive Director')}&url=${encodeURIComponent(shareUrlFor('ed-announcement'))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                        aria-label="Share on X (Twitter)"
                      >
                        <Twitter className="w-4 h-4" />
                        X
                      </a>
                      <a
                        data-testid="share-facebook-announcement"
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrlFor('ed-announcement'))}&quote=${encodeURIComponent('CommonSense Democrats Appoints Derrell Simpson as Executive Director')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                        aria-label="Share on Facebook"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                      <a
                        data-testid="share-linkedin-announcement"
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrlFor('ed-announcement'))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                        aria-label="Share on LinkedIn"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                      <a
                        data-testid="share-email-announcement"
                        href={`mailto:?subject=${encodeURIComponent('CommonSense Democrats Appoints Derrell Simpson as Executive Director')}&body=${encodeURIComponent('Thought you would want to read this announcement from CommonSense Democrats:\n\nCommonSense Democrats Appoints Derrell Simpson as Executive Director\n\n' + shareUrlFor('ed-announcement'))}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                        aria-label="Share via Email"
                      >
                        <Mail className="w-4 h-4" />
                        Email
                      </a>
                      <a
                        data-testid="share-sms-announcement"
                        href={`sms:?&body=${encodeURIComponent('CommonSense Democrats Appoints Derrell Simpson as Executive Director: ' + shareUrlFor('ed-announcement'))}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                        aria-label="Share via SMS"
                      >
                        <MessageSquare className="w-4 h-4" />
                        SMS
                      </a>
                      <button
                        data-testid="share-copy-announcement"
                        type="button"
                        onClick={() => {
                          const url = shareUrlFor('ed-announcement');
                          if (navigator.clipboard) {
                            navigator.clipboard.writeText(url);
                          }
                          toast({
                            title: 'Link copied',
                            description: 'Share it with your network.'
                          });
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                        aria-label="Copy link"
                      >
                        <Link2 className="w-4 h-4" />
                        Copy Link
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button 
                  variant="outline"
                  className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-semibold"
                  onClick={() => window.open('https://secure.actblue.com/donate/commonsense-democrats---ie-1', '_blank')}
                >
                  Support Our Work
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Executive Director Profile Section - archived, gated by SHOW_ED_CONTENT */}
      {SHOW_ED_CONTENT && (
      <section className="py-12 px-6 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-6 text-center">Meet Our Executive Director</h2>
          <p className="text-xl text-gray-300 mb-12 text-center">
            Learn more about Derrell Simpson's background, experience, and vision for CommonSense Democrats
          </p>

          <Tabs defaultValue="biography" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800 mb-8">
              <TabsTrigger value="biography" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Biography
              </TabsTrigger>
              <TabsTrigger value="qa" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Q&A
              </TabsTrigger>
            </TabsList>

            <TabsContent value="biography">
              <Dialog>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-2xl">Derrell Simpson - Executive Director</CardTitle>
                    <CardDescription className="text-gray-400">
                      CommonSense Democrats
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-gray-300 leading-relaxed">
                    <p>
                      Leadership is often measured by the positions a person holds. Derrell Simpson has built his career by the impact he leaves behind. For more than two decades, Simpson has served at the intersection of public service, education, government, business, and political strategy, guided by a simple philosophy: listen first, build partnerships, and focus on results that improve people's lives.
                    </p>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        data-testid="bio-read-more"
                        className="inline-flex items-center gap-2 px-5 py-2.5 mt-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
                      >
                        Read Full Biography
                      </button>
                    </DialogTrigger>
                  </CardContent>
                </Card>
                <DialogContent className="max-w-3xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-white pr-8">Derrell Simpson — Executive Director</DialogTitle>
                    <p className="text-gray-400 text-sm">CommonSense Democrats</p>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      <p>
                    Leadership is often measured by the positions a person holds. Derrell Simpson has built his career by the impact he leaves behind.
                  </p>
                  <p>
                    For more than two decades, Simpson has served at the intersection of public service, education, government, business, and political strategy. Whether working with neighborhood organizations, advising elected officials, leading public initiatives, or helping candidates earn the trust of their communities, his approach has remained consistent: listen first, build partnerships, and focus on results that improve people's lives.
                  </p>
                  <p>
                    Today, as Executive Director of CommonSense Democrats, Simpson brings that philosophy to an organization dedicated to strengthening civic engagement, supporting principled leadership, and advancing practical solutions to the challenges facing communities across America.
                  </p>
                  
                  <h4 className="text-white font-semibold text-lg mt-6">Early Career in Education</h4>
                  <p>
                    A native of Washington, D.C., Simpson's commitment to public service began early. After graduating from Talladega College, he chose education as the foundation of his career, teaching kindergarten and later fourth and fifth grade reading and writing through the Community School Program at JC Nalle Elementary School. The classroom became his first lesson in leadership, reinforcing the importance of meeting people where they are and creating opportunities that allow every individual to succeed.
                  </p>
                  <p>
                    That commitment led him to the National Center for Children and Families, where he served as Interim Director of its Community School initiative. There, Simpson expanded partnerships between schools, families, and community organizations while introducing trauma informed professional development and new parent engagement programs designed to strengthen outcomes for students. His work reflected a belief that successful communities are built through collaboration rather than isolated institutions.
                  </p>

                  <h4 className="text-white font-semibold text-lg mt-6">Public Service Leadership</h4>
                  <p>
                    In 2003, Simpson was appointed by District of Columbia Mayor Anthony Williams to the District of Columbia Commission on National and Community Service, becoming the youngest individual to assume fiduciary responsibility for the commission. During his tenure, he helped oversee Serve DC, strengthened AmeriCorps programming, expanded volunteer initiatives, and championed partnerships that introduced service learning throughout the District's public school system. Among the accomplishments he values most is creating the Commander Ready Program, an emergency preparedness initiative designed specifically for children and families.
                  </p>
                  <p>
                    As the nation entered the Great Recession, Simpson accepted an appointment as Special Assistant to the City Manager for Intergovernmental Affairs in Talladega, Alabama. Charged with helping local government navigate unprecedented economic challenges, he authored the city's first public private partnership policy, secured a United States Environmental Protection Agency Brownfields Grant that launched redevelopment of a downtown site, and helped lead initiatives that improved parks, recreational facilities, sidewalks, bicycle infrastructure, and public health programs serving underserved communities.
                  </p>
                  <p>
                    Returning to Washington, Simpson continued his commitment to public service at the Council of the District of Columbia, serving first as Senior Advisor to an At Large Councilmember and later as Special Assistant to the Council Chairman. Working alongside legislative leadership, he helped develop strategies supporting landmark legislation that strengthened workplace protections, expanded educational opportunities, and improved economic security for District residents.
                  </p>

                  <h4 className="text-white font-semibold text-lg mt-6">Business & Organizational Leadership</h4>
                  <p>
                    While public service has defined much of his career, Simpson has also established himself as a respected business and organizational leader.
                  </p>
                  <p>
                    He is a founding partner of APG, LLC, a strategic consulting firm specializing in political strategy, communications, stakeholder engagement, and organizational development. Through his work with businesses, nonprofit organizations, advocacy groups, and political campaigns, Simpson has helped clients strengthen public trust, expand community engagement, and navigate complex policy and communications challenges.
                  </p>
                  <p>
                    His entrepreneurial experience also includes co-founding TechParking, Inc., a transportation technology company focused on improving municipal parking management through satellite tracking technology. In addition, he has served on the boards of several public relations, government relations, and nonprofit organizations, contributing strategic guidance that has strengthened organizational growth, public engagement, and operational effectiveness.
                  </p>

                  <h4 className="text-white font-semibold text-lg mt-6">Political Organizing</h4>
                  <p>
                    Political organizing has remained a constant throughout Simpson's career.
                  </p>
                  <p>
                    Raised in Washington's historic Trinidad neighborhood, he began organizing in his own community, leading successful efforts to secure public investment, improve neighborhood facilities, and strengthen civic participation. Those early experiences established the leadership style that continues to define his work today.
                  </p>
                  <p>
                    Since 2002, Simpson has managed or advised campaigns in nearly every election cycle in the District of Columbia while supporting candidates and organizations throughout Maryland, Virginia, Alabama, Michigan, and beyond. His expertise spans campaign strategy, voter outreach, coalition building, field operations, strategic communications, endorsement strategy, and grassroots mobilization. He has also served on the media team for the 2009 Presidential Inaugural Committee during the inauguration of President Barack Obama and Vice President Joseph Biden.
                  </p>

                  <h4 className="text-white font-semibold text-lg mt-6">Recognition & Leadership Today</h4>
                  <p>
                    Throughout his career, Simpson has received recognition for both leadership and service. His honors include national recognition from Edutopia Magazine, Black Entertainment Television, the National Business League, Tuskegee University, and numerous public service awards from local and federal officials. While grateful for those distinctions, Simpson believes the most meaningful measure of success is the opportunity to improve the lives of others through service and leadership.
                  </p>
                  <p>
                    As Executive Director of CommonSense Democrats, Simpson now leads an organization committed to supporting leaders who believe effective government requires accountability, collaboration, and common sense. He is responsible for guiding the organization's strategic direction, strengthening partnerships, expanding voter engagement, supporting candidates, and helping build a broader movement focused on practical Democratic leadership.
                  </p>
                  <p className="font-semibold text-white text-lg mt-4">
                    For Simpson, leadership has never been about holding office or seeking recognition. It has always been about earning trust.
                  </p>
                  <p>
                    That philosophy has guided every chapter of his career and continues to shape his vision for CommonSense Democrats as the organization expands its reach and works to strengthen communities across the country.
                  </p>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="qa">
              <Dialog>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-2xl">Meet Derrell Simpson</CardTitle>
                    <CardDescription className="text-gray-400">
                      Executive Director, CommonSense Democrats
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">What inspired you to become Executive Director of CommonSense Democrats?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Throughout my career, I've been fortunate to work in education, government, business, and politics, but every role has been driven by the same purpose: serving people. When I spoke with Sean about the future of CommonSense Democrats, I saw an opportunity to help build an organization that focuses on practical leadership, meaningful civic engagement, and real solutions. That mission aligns closely with the values that have guided my career.
                    </p>
                  </div>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        data-testid="qa-read-more"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
                      >
                        Read Full Q&amp;A
                      </button>
                    </DialogTrigger>
                  </CardContent>
                </Card>
                <DialogContent className="max-w-3xl max-h-[85vh] bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-white pr-8">Meet Derrell Simpson</DialogTitle>
                    <p className="text-gray-400 text-sm">Executive Director, CommonSense Democrats</p>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">What inspired you to become Executive Director of CommonSense Democrats?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Throughout my career, I've been fortunate to work in education, government, business, and politics, but every role has been driven by the same purpose: serving people. When I spoke with Sean about the future of CommonSense Democrats, I saw an opportunity to help build an organization that focuses on practical leadership, meaningful civic engagement, and real solutions. That mission aligns closely with the values that have guided my career.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">You've spent more than two decades in public service and political strategy. What has that experience taught you?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      I've learned that people want to be heard. Whether you're working with a neighborhood association, a nonprofit organization, or an elected official, progress begins with listening. Successful leadership isn't about having all the answers. It's about bringing people together, earning trust, and creating solutions that improve lives.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">Why do you believe organizations like CommonSense Democrats matter today?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Many people have grown frustrated with politics because they feel disconnected from the process. Organizations like CommonSense Democrats help bridge that gap by educating voters, supporting thoughtful candidates, and creating opportunities for people to engage in meaningful ways. Democracy is strongest when communities are informed, involved, and empowered.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">What are your priorities as Executive Director?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      My immediate priorities are expanding our outreach, strengthening community partnerships, increasing voter engagement, and supporting candidates who are committed to effective, accountable leadership. I also want to build an organization that people trust, not only during election season, but throughout the year.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">You've worked in education, government, business, and political campaigns. How have those experiences shaped your leadership style?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Each experience reinforced the same lesson: relationships matter. Whether you're teaching students, leading a government initiative, or advising a campaign, success depends on collaboration. My leadership style is grounded in listening, building consensus, and helping people work toward common goals.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">What do you believe voters are looking for from leaders today?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      People are looking for authenticity. They want leaders who communicate honestly, accept responsibility, and stay focused on solving problems instead of creating them. They want results, not rhetoric.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">What role should civic engagement play outside of election years?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Civic engagement should never be limited to campaign season. Strong communities are built through consistent participation, whether that's volunteering, attending community meetings, mentoring young people, or simply staying informed. Democracy isn't something we practice every few years. It's something we practice every day.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">How do you define effective leadership?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Leadership isn't about position or recognition. It's about earning trust. It's about showing up consistently, making thoughtful decisions, and putting the interests of the people you serve ahead of your own. When people trust their leaders, meaningful progress becomes possible.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">What message would you like to share with supporters of CommonSense Democrats?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Thank you for believing that public service still matters. Every volunteer, donor, supporter, and community partner plays an important role in strengthening our democracy. Together, we can build an organization that empowers communities, supports principled leadership, and creates lasting impact.
                    </p>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h4 className="text-blue-400 font-semibold mb-3">Looking ahead, what excites you most about the future?</h4>
                    <p className="text-gray-300 leading-relaxed">
                      I'm excited about the opportunity to build something lasting. CommonSense Democrats has a clear mission, a growing network of supporters, and an opportunity to make a meaningful difference in communities across the country. I look forward to helping shape that future alongside our members, candidates, partners, and volunteers.
                    </p>
                  </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      )}

      {/* About Section */}
      <section className="py-12 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-white mb-6 text-center">Who We Are. Who We Support.</h2>
          <p className="text-lg text-gray-300 mb-12 text-center max-w-4xl mx-auto leading-relaxed">
            CommonSense Democrats is a national political action committee built and led by experienced campaign operatives who have served in every position of a campaign, from field organizing and finance to communications, data, operations, and senior strategy. We have run races at every level, won tough fights, and seen firsthand what it takes to support candidates and serve communities. That experience drives everything we do. We back leaders who reflect the values shown below, the kind of leaders our neighborhoods deserve.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              { slug: "accountability-ethics", title: "Accountability & Ethics" },
              { slug: "economic-stability", title: "Economic Stability" },
              { slug: "evidence-based-policy", title: "Evidence-Based Policy" },
              { slug: "inclusive-leadership", title: "Inclusive Leadership" },
              { slug: "local-first-thinking", title: "Local-First Thinking" },
              { slug: "practical-solutions", title: "Practical Solutions" }
            ].map((item) => (
              <Dialog key={item.slug}>
                <DialogTrigger asChild>
                  <button
                    data-testid={`who-card-${item.slug}`}
                    type="button"
                    className="group relative block w-full overflow-hidden rounded-lg border border-slate-700 hover:border-blue-500 transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-400"
                    aria-label={`Enlarge ${item.title} image`}
                  >
                    <img
                      src={`/who/${item.slug}.jpg`}
                      alt={`${item.title} — CommonSense Democrats supports leaders who lead with ${item.title.toLowerCase()}`}
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                    <span className="pointer-events-none absolute inset-0 flex items-end justify-center bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-white text-sm font-semibold pb-3 uppercase tracking-wider">Click to enlarge</span>
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-5xl bg-slate-800 border-slate-700 p-2 sm:p-4">
                  <DialogTitle className="sr-only">{item.title}</DialogTitle>
                  <img
                    src={`/who/${item.slug}.jpg`}
                    alt={item.title}
                    data-testid={`who-modal-image-${item.slug}`}
                    className="w-full h-auto rounded"
                  />
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
      </section>

      {/* Policy Priorities Section */}
      <section className="py-12 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-white mb-6 text-center">Policy Priorities</h2>
          <p className="text-xl text-gray-300 mb-12 text-center max-w-3xl mx-auto">
            We focus on issues that impact everyday Americans
          </p>

          <div className="space-y-8">
            {policyPriorities.map((policy, idx) => {
              const totalPoints = policy.groups
                ? policy.groups.reduce((n, g) => n + g.points.length, 0)
                : (policy.points || []).length;
              const isExpanded = !!expandedPolicies[idx];
              return (
              <Card key={idx} className="bg-slate-800/80 border-slate-700 hover:border-blue-500 transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-center gap-4 text-2xl">
                    <div className="text-blue-400">{policy.icon}</div>
                    {policy.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {policy.intro && (
                    <p className="text-gray-300 leading-relaxed max-w-4xl mx-auto text-center mb-6 italic">
                      {policy.intro}
                    </p>
                  )}
                  {policy.groups ? (
                    <div className="max-w-4xl mx-auto">
                      {policy.groups.map((group, gidx) => {
                        const shownPoints = (isExpanded || gidx === 0)
                          ? group.points
                          : [];
                        if (!isExpanded && gidx > 0) return null;
                        return (
                          <div key={gidx} className={gidx > 0 ? 'mt-6' : ''}>
                            <h4 className="text-blue-400 font-semibold text-lg mb-3 text-center uppercase tracking-wider">
                              {group.label}
                            </h4>
                            <ul className="space-y-3">
                              {shownPoints.map((point, pidx) => (
                                <li key={pidx} className="text-gray-300 flex items-start gap-3">
                                  <CheckCircle2 className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                      <div className="flex justify-center mt-6">
                        <button
                          type="button"
                          data-testid={`policy-toggle-${idx}`}
                          onClick={() => setExpandedPolicies(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
                        >
                          {isExpanded ? 'Show less' : `Show all ${totalPoints} positions`}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <ul className="space-y-3 max-w-4xl mx-auto">
                      {policy.points.map((point, pidx) => (
                        <li key={pidx} className="text-gray-300 flex items-start gap-3 justify-center text-center">
                          <CheckCircle2 className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                          <span className="text-left">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section id="get-involved" className="py-12 px-6 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold text-white mb-6 text-center">Join the Movement</h2>
          <p className="text-xl text-gray-300 mb-12 text-center">
            CommonSense Democrats is powered by people like you who believe in common-sense governance
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              {
                key: "donate",
                title: "Donate to Support Our Cause",
                desc: "Every contribution helps us back strong leaders",
                href: "#donate",
                type: "link"
              },
              {
                key: "volunteer",
                title: "Sign Up to Get Involved",
                desc: "Join our network of volunteers, organizers, and supporters.",
                href: "https://docs.google.com/forms/d/e/1FAIpQLSe2-E4XJdV4TtDNVNd3fAzZhC9fmeLs3ru2ElKKj5NrOuZPIA/viewform?usp=publish-editor",
                type: "external"
              },
              {
                key: "nominate",
                title: "Nominate a Candidate",
                desc: "Know a CommonSense leader who should run? Tell us about them.",
                href: "https://docs.google.com/forms/d/e/1FAIpQLSeTORNWxTJftbdfveXmTir-Pau62VNcDP4oaE-Piv-Db5VDmw/viewform?usp=dialog",
                type: "external"
              },
              {
                key: "shop",
                title: "Shop CommonSense Merch",
                desc: "Wear the movement. Every order helps fund our work.",
                href: "https://commonsensedemocrats.printful.me",
                type: "external"
              },
              {
                key: "spread",
                title: "Spread the Word",
                desc: "Share our message with your community and networks",
                type: "share",
                wrapperClass: "md:col-span-2"
              }
            ].map((item) => {
              const cardInner = (
                <Card className="h-full bg-slate-800 border-slate-700 group-hover:border-blue-500 transition-all duration-300 group-hover:scale-[1.02]">
                  <CardHeader>
                    <CardTitle className="text-blue-400">{item.title}</CardTitle>
                    <CardDescription className="text-gray-300">{item.desc}</CardDescription>
                  </CardHeader>
                </Card>
              );
              if (item.type === "share") {
                return (
                  <Dialog key={item.key}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        data-testid={`join-${item.key}-card`}
                        className={`group block w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg ${item.wrapperClass || ''}`}
                        aria-label="Open share dialog"
                      >
                        {cardInner}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
                      <DialogHeader>
                        <DialogTitle className="text-2xl text-white">Spread the Word</DialogTitle>
                        <p className="text-gray-400 text-sm">Share CommonSense Democrats with your network.</p>
                      </DialogHeader>
                      <div className="flex flex-wrap items-center gap-3 pt-2">
                        <a
                          data-testid="spread-twitter"
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Investing in a Better, Bolder Democratic Future. CommonSense Democrats backs pragmatic leaders who win tough races and govern with integrity.')}&url=${encodeURIComponent(PUBLIC_SITE_URL + '/')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                          aria-label="Share on X"
                        >
                          <Twitter className="w-4 h-4" /> X
                        </a>
                        <a
                          data-testid="spread-facebook"
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(PUBLIC_SITE_URL + '/')}&quote=${encodeURIComponent('CommonSense Democrats — Leaders with Common Sense.')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                          aria-label="Share on Facebook"
                        >
                          <Facebook className="w-4 h-4" /> Facebook
                        </a>
                        <a
                          data-testid="spread-linkedin"
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(PUBLIC_SITE_URL + '/')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                          aria-label="Share on LinkedIn"
                        >
                          <Linkedin className="w-4 h-4" /> LinkedIn
                        </a>
                        <a
                          data-testid="spread-email"
                          href={`mailto:?subject=${encodeURIComponent('CommonSense Democrats — Leaders with Common Sense')}&body=${encodeURIComponent('I wanted to share CommonSense Democrats with you. They support pragmatic, solutions-oriented leaders who win tough races and govern with integrity.\n\n' + PUBLIC_SITE_URL + '/')}`}
                          className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                          aria-label="Share via Email"
                        >
                          <Mail className="w-4 h-4" /> Email
                        </a>
                        <a
                          data-testid="spread-sms"
                          href={`sms:?&body=${encodeURIComponent('CommonSense Democrats — Leaders with Common Sense: ' + PUBLIC_SITE_URL + '/')}`}
                          className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                          aria-label="Share via SMS"
                        >
                          <MessageSquare className="w-4 h-4" /> SMS
                        </a>
                        <button
                          data-testid="spread-copy"
                          type="button"
                          onClick={() => {
                            const url = PUBLIC_SITE_URL + '/';
                            if (navigator.clipboard) {
                              navigator.clipboard.writeText(url);
                            }
                            toast({ title: 'Link copied', description: 'Share it with your network.' });
                          }}
                          className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                          aria-label="Copy link"
                        >
                          <Link2 className="w-4 h-4" /> Copy Link
                        </button>
                      </div>
                    </DialogContent>
                  </Dialog>
                );
              }
              return (
                <a
                  key={item.key}
                  href={item.href}
                  data-testid={`join-${item.key}-card`}
                  className={`group block focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg ${item.wrapperClass || ''}`}
                  {...(item.type === 'external' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {cardInner}
                </a>
              );
            })}
          </div>

        </div>
      </section>

      {/* Our Perspective Section */}
      <section className="py-12 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-white mb-6 text-center">Our Perspective</h2>
          <p className="text-xl text-gray-300 mb-12 text-center max-w-3xl mx-auto">
            Insights and commentary on pragmatic leadership and policy solutions
          </p>

          {/* Featured articles: full-width landscape cards */}
          {articles.filter(a => a.featured).map((article) => {
            const previewText = (article.excerpt || article.content).replace(/\s+/g, ' ').trim();
            const showByline = article.author && article.author !== 'CommonSense Democrats';
            return (
              <Dialog key={`featured-${article.id}`} open={openModal === article.slug} onOpenChange={(o) => handleModalChange(article.slug, o)}>
                <DialogTrigger asChild>
                  <Card
                    data-testid={`article-featured-${article.slug}`}
                    className="mb-8 bg-slate-800 border-2 border-blue-500 hover:border-blue-400 transition-all duration-300 cursor-pointer overflow-hidden md:grid md:grid-cols-5 gap-0"
                  >
                    {article.image && (
                      <div className="md:col-span-2 w-full aspect-video md:aspect-auto bg-slate-900 border-b md:border-b-0 md:border-r border-slate-700 flex items-center justify-center overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="md:col-span-3 flex flex-col justify-center p-6 md:p-8">
                      <span className="inline-block self-start px-3 py-1 mb-3 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-widest">
                        Featured
                      </span>
                      <h3 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">{article.title}</h3>
                      {showByline && (
                        <p
                          data-testid={`article-featured-byline-${article.slug}`}
                          className="text-sm text-blue-400 font-medium mb-4"
                        >
                          By {article.author}
                        </p>
                      )}
                      <p className="text-gray-300 leading-relaxed line-clamp-4 mb-4">{previewText}</p>
                      <Button variant="link" className="text-blue-400 px-0 self-start">
                        Read Full Article →
                      </Button>
                    </div>
                  </Card>
                </DialogTrigger>
                {/* Modal content is rendered inside the regular grid map below (dedupe by id), so we reuse that dialog. Nothing else to render here. */}
              </Dialog>
            );
          })}

          <div className="grid md:grid-cols-3 gap-6">
            {articles.map((article) => {
              const previewText = (article.excerpt || article.content).replace(/\s+/g, ' ').trim();
              const showByline = article.author && article.author !== 'CommonSense Democrats';
              return (
              <Dialog key={article.id} open={openModal === article.slug} onOpenChange={(o) => handleModalChange(article.slug, o)}>
                <DialogTrigger asChild>
                  <Card
                    data-testid={`article-card-${article.slug}`}
                    className={`bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300 cursor-pointer hover:scale-[1.02] overflow-hidden flex flex-col ${article.featured ? 'hidden' : ''}`}
                  >
                    {article.image && (
                      <div className="w-full aspect-video bg-slate-900 border-b border-slate-700 flex items-center justify-center overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-lg leading-snug">{article.title}</CardTitle>
                      {showByline && (
                        <p
                          data-testid={`article-byline-${article.slug}`}
                          className="text-xs text-blue-400 font-medium mt-1"
                        >
                          By {article.author}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-between pt-0">
                      <p className="text-gray-300 text-sm leading-relaxed line-clamp-3">{previewText}</p>
                      <Button variant="link" className="text-blue-400 px-0 mt-2 self-start text-sm">
                        Read More →
                      </Button>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-white pr-8">{article.title}</DialogTitle>
                    <p className="text-gray-400 text-sm">By {article.author}</p>
                  </DialogHeader>
                  <ScrollArea className="max-h-[60vh] pr-4">
                    {article.image && (
                      <img
                        src={article.image}
                        alt={article.title}
                        data-testid={`article-image-${article.id}`}
                        className="w-full rounded-lg mb-6 border border-slate-700"
                        loading="lazy"
                      />
                    )}
                    <div className="space-y-4 text-gray-300 leading-relaxed whitespace-pre-line">
                      {article.content}
                    </div>
                    {article.cta && (
                      <div className="mt-6 p-5 rounded-lg bg-blue-950 border-2 border-blue-500">
                        <p className="text-gray-300 text-sm leading-relaxed mb-4">{article.cta.note}</p>
                        <a
                          data-testid={`article-cta-${article.slug}`}
                          href={article.cta.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold uppercase tracking-wider transition-colors shadow-lg"
                        >
                          {article.cta.label} →
                        </a>
                      </div>
                    )}
                  </ScrollArea>
                  <div className="border-t border-slate-700 pt-4 mt-2 flex flex-wrap items-center gap-3">
                    <span className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Share:</span>
                    <a
                      data-testid={`share-twitter-${article.id}`}
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title + ' — CommonSense Democrats')}&url=${encodeURIComponent(shareUrlFor(article.slug))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      aria-label="Share on X (Twitter)"
                    >
                      <Twitter className="w-4 h-4" />
                      X
                    </a>
                    <a
                      data-testid={`share-facebook-${article.id}`}
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrlFor(article.slug))}&quote=${encodeURIComponent(article.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      aria-label="Share on Facebook"
                    >
                      <Facebook className="w-4 h-4" />
                      Facebook
                    </a>
                    <a
                      data-testid={`share-linkedin-${article.id}`}
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrlFor(article.slug))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      aria-label="Share on LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </a>
                    <a
                      data-testid={`share-email-${article.id}`}
                      href={`mailto:?subject=${encodeURIComponent(article.title + ' — CommonSense Democrats')}&body=${encodeURIComponent('Thought you would want to read this from CommonSense Democrats:\n\n' + article.title + '\n\n' + shareUrlFor(article.slug))}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      aria-label="Share via Email"
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </a>
                    <a
                      data-testid={`share-sms-${article.id}`}
                      href={`sms:?&body=${encodeURIComponent(article.title + ' — CommonSense Democrats: ' + shareUrlFor(article.slug))}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      aria-label="Share via SMS"
                    >
                      <MessageSquare className="w-4 h-4" />
                      SMS
                    </a>
                    <button
                      data-testid={`share-copy-${article.id}`}
                      type="button"
                      onClick={() => {
                        const url = shareUrlFor(article.slug);
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(url);
                        }
                        toast({
                          title: 'Link copied',
                          description: 'Share it with your network.'
                        });
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      aria-label="Copy link"
                    >
                      <Link2 className="w-4 h-4" />
                      Copy Link
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
              );
            })}
          </div>
        </div>
      </section>

      {/* Donation Section - Fuel Independent Leadership */}
      <section id="donate" className="py-12 px-6 bg-gradient-to-b from-slate-900 to-blue-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold text-white mb-6 text-center">Fuel Independent Leadership</h2>
          
          <Card className="bg-slate-800/90 border-slate-700 mb-8">
            <CardContent className="pt-6">
              <p className="text-xl text-gray-200 leading-relaxed">
                CommonSense Democrats is structured as a <span className="text-blue-400 font-semibold">Hybrid PAC</span>, which means we operate two legally separate accounts under federal law.
              </p>
              <p className="text-xl text-gray-200 mt-4 leading-relaxed">
                However, the engine of our impact is our <span className="text-blue-400 font-semibold">Independent Expenditure Account</span>.
              </p>
              <p className="text-2xl text-white mt-6 font-semibold text-center">
                That is where your support makes the greatest difference.
              </p>
            </CardContent>
          </Card>

          {/* Why IE Matters */}
          <Card className="bg-gradient-to-br from-blue-900/50 to-slate-800/90 border-blue-500/50 mb-8">
            <CardHeader>
              <CardTitle className="text-white text-3xl">Why the Independent Expenditure Account Matters</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-200 mb-6 leading-relaxed">
                Federal law allows political committees to operate an account dedicated exclusively to independent advocacy. This account allows us to:
              </p>
              <ul className="space-y-3">
                {[
                  "Raise unlimited contributions from individuals",
                  "Accept corporate support",
                  "Fund digital advertising, streaming, mail, voter education, and large-scale messaging",
                  "Support candidates without coordinating with their campaigns"
                ].map((item, idx) => (
                  <li key={idx} className="text-gray-200 flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-100 mt-6 text-lg leading-relaxed font-medium">
                Independent expenditures allow us to communicate directly with voters at scale. They allow us to act quickly. They allow us to compete.
              </p>
              <p className="text-white mt-4 text-xl font-semibold">
                If you want CommonSense Democrats to amplify pragmatic leadership across the country, this is the account that powers that work.
              </p>
            </CardContent>
          </Card>

          {/* Primary CTA - IE Account */}
          <div className="text-center my-12">
            <h3 className="text-3xl font-bold text-white mb-4">Donate to the Independent Expenditure Account</h3>
            <p className="text-gray-300 mb-6 text-lg max-w-3xl mx-auto">
              When you click Donate, you will be directed to contribute to our Independent Expenditure Account, where your support has the greatest reach and flexibility under federal law.
            </p>
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-6 text-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/50"
              onClick={() => window.open('https://secure.actblue.com/donate/commonsense-democrats---ie-1', '_blank')}
            >
              DONATE NOW
            </Button>
          </div>

          {/* Contribution Details */}
          <Card className="bg-slate-800/70 border-slate-700 mb-8">
            <CardHeader>
              <CardTitle className="text-white text-2xl">Contribution Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <p className="text-lg">
                <span className="text-blue-400 font-semibold">There is no contribution limit</span> for the Independent Expenditure Account.
              </p>
              <p>
                All contributions are publicly disclosed in accordance with Federal Election Commission regulations. Federal law requires us to collect contributor name, address, occupation, and employer.
              </p>
              <p>
                Contributions are not tax deductible.
              </p>
              <p>
                Independent expenditures are not made in coordination with any candidate or campaign.
              </p>
            </CardContent>
          </Card>

          {/* Donor Tiers */}
          <Card className="bg-slate-800/70 border-slate-700 mb-8">
            <CardHeader>
              <CardTitle className="text-white text-2xl">Donor Recognition Tiers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {donorTiers.map((tier, idx) => (
                  <div key={idx} className="bg-slate-700/50 p-4 rounded-lg border border-slate-600 hover:border-blue-500 transition-colors">
                    <h4 className="text-blue-400 font-semibold text-lg">{tier.name}</h4>
                    <p className="text-white font-semibold">{tier.amount}</p>
                    <p className="text-gray-300 text-sm mt-2">{tier.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Secondary Account Info */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl">What About the Other Account?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <p className="leading-relaxed">
                As a Hybrid PAC, we also maintain a separate <span className="text-blue-400">Federal Contribution Account</span> that can make limited direct contributions to candidates.
              </p>
              <p className="leading-relaxed">
                That account is subject to a <span className="text-white font-semibold">$5,000 annual limit per individual</span> and cannot accept corporate contributions.
              </p>
              <p className="leading-relaxed text-gray-400 italic">
                While important, it is not the primary vehicle for scaling our impact.
              </p>
              <div className="pt-4">
                <Button 
                  variant="outline"
                  className="border border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  onClick={() => window.open('https://secure.actblue.com/donate/commonsense-democrats-1', '_blank')}
                >
                  Donate to Federal Contribution Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer with FEC Disclaimer */}
      <footer className="bg-gradient-to-b from-blue-900 to-slate-950 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <img 
              src="/logo.png" 
              alt="CommonSense Democrats Logo" 
              className="w-48 md:w-56 mx-auto mb-4 filter drop-shadow-lg"
            />
            <p className="text-gray-400 mb-4">Investing in a Better, Bolder Democratic Future</p>
            <div className="flex gap-6 justify-center mb-6">
              <a 
                href="mailto:hello@commonsensedemocrats.us"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                <Mail className="w-6 h-6" />
              </a>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8">
            <p className="text-gray-500 text-sm text-center mb-4">
              Paid for by CommonSense Democrats. Not authorized by any candidate or candidate's committee.
            </p>
            <p className="text-gray-500 text-xs text-center">
              CommonSense Democrats is a hybrid political action committee registered with the Federal Election Commission.<br />
              1717 N Street, NW, Suite 1, Washington, DC 20036
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs">
              <a
                href="/privacy"
                data-testid="footer-privacy-link"
                className="text-gray-400 hover:text-blue-400 underline underline-offset-2 transition-colors"
              >
                Privacy Policy &amp; SMS Terms
              </a>
              <a
                href="mailto:hello@commonsensedemocrats.us"
                className="text-gray-400 hover:text-blue-400 underline underline-offset-2 transition-colors"
              >
                Contact Us
              </a>
            </div>
            <p className="text-gray-600 text-xs text-center mt-4">
              © {new Date().getFullYear()} CommonSense Democrats. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;